import type {
  ListPeopleOpts,
  PeoplePageResult,
  User,
  UserRole,
  OtpPurpose,
  OtpRequestResult,
} from '../types/auth';
import { assignableRoles, isAdminLike, isOwner } from '../lib/roles';
import { fromFaDigits, toFaDigits } from '../i18n';
import type { ClassFile, CoachProfile, PersonDetail, RosterRow, StudentProfile } from '../types/identity';
import type {
  ActivityFeedItem,
  AttendanceRecord,
  AttendanceRegisterRow,
  BranchLocation,
  BranchLocationInput,
  ClassOffering,
  ClassSession,
  CreateClassPayload,
  CreateDiscountPayload,
  CreateSessionPayload,
  DiscountTargetType,
  Enrollment,
  EnrollmentDiscount,
  EventRegistration,
  InstituteContact,
  InstituteEvent,
  InstituteSettings,
  LeavePolicy,
  LeaveRequest,
  RecordAttendancePayload,
  RegistrationDiscount,
  StudentTimeline,
  StaffVisual,
  TeachingContent,
  TimelineItem,
} from '../types/academic';
import type { CreateNotificationPayload, NotificationRecord } from '../types/notifications';
import type { Announcement, CreateAnnouncementPayload } from '../types/announcements';
import type {
  BillingSummary,
  CoachAgreement,
  CompensationResponse,
  InstituteOverview,
  LedgerEntry,
  LedgerKind,
  LedgerPeriod,
  LedgerSource,
  PaymentMethod,
  PaymentSettings,
  PricingPolicy,
  StudentInvoice,
} from '../types/billing';
import type {
  AdminDashboard,
  CoachDashboard,
  DashboardScope,
  DashboardSession,
  OperatorDashboard,
  RevenueShareConfig,
  RoleDashboard,
  SessionContext,
  StudentDashboard,
} from '../types/dashboard';
import { resolveScopeBounds } from '../lib/time';
import {
  countLeaveRequestsInSeason,
  DEFAULT_LEAVE_POLICY,
  inclusiveDays,
  normalizeLeavePolicy,
  seasonBounds,
} from '../lib/leavePolicy';
import { classHasStaff, coachIdsOf, assistantIdsOf, staffIdsOf } from '../lib/classStaff';

const DANCE_LEVEL_RANK: Record<string, number> = {
  beginner: 1,
  intermediate: 2,
  advanced: 3,
};

function isClassLevelAccessible(studentLevel?: string, classLevel?: string): boolean {
  if (!classLevel || classLevel === 'open') return true;
  if (!studentLevel || studentLevel === 'open') {
    return classLevel === 'beginner' || classLevel === 'open';
  }
  const studentRank = DANCE_LEVEL_RANK[studentLevel] ?? 1;
  const classRank = DANCE_LEVEL_RANK[classLevel] ?? 1;
  return classRank <= studentRank;
}
import {
  DEMO_AGREEMENTS,
  DEMO_ANNOUNCEMENTS,
  DEMO_ATTENDANCE,
  DEMO_CLASSES,
  DEMO_ENROLLMENTS,
  DEMO_EVENT_REGS,
  DEMO_EVENTS,
  DEMO_INVOICES,
  DEMO_LEDGER,
  DEMO_LEAVE_REQUESTS,
  DEMO_LOCATIONS,
  DEMO_NOTIFICATIONS,
  DEMO_USERS,
} from './demoData';
import {
  buildDemoSessionsForToday,
  cloneDemoData,
  normalizeSessionsForToday,
} from './demoSessionUtils';

const STORAGE_KEY = 'dancemance_demo_state';
const STORAGE_VERSION = 17;

interface DemoState {
  version: number;
  people: User[];
  studentProfiles: StudentProfile[];
  coachProfiles: CoachProfile[];
  files: ClassFile[];
  classes: ClassOffering[];
  locations: BranchLocation[];
  enrollments: Enrollment[];
  sessions: ClassSession[];
  attendance: AttendanceRecord[];
  leaveRequests: LeaveRequest[];
  events: InstituteEvent[];
  eventRegs: EventRegistration[];
  notifications: NotificationRecord[];
  announcements: Announcement[];
  progressNotes: Array<{ studentId: string; item: TimelineItem }>;
  invoices: StudentInvoice[];
  ledger: LedgerEntry[];
  agreements: CoachAgreement[];
  pricing: PricingPolicy;
  settings: PaymentSettings;
  leavePolicy: LeavePolicy;
  registrationTelegram: string;
  registrationOpen: boolean;
  discounts: RegistrationDiscount[];
  shareConfigs: RevenueShareConfig[];
}

function freshState(): DemoState {
  return {
    version: STORAGE_VERSION,
    people: cloneDemoData(DEMO_USERS),
    studentProfiles: [
      {
        userId: 'demo-student-001',
        danceLevel: 'beginner',
        danceLevels: {
          salsa: 'advanced',
          bachata: 'beginner',
        },
        emergencyName: 'Leila Student',
        emergencyPhone: '09120000099',
        notes: 'Prefers evening classes.',
      },
    ],
    coachProfiles: [
      {
        userId: 'demo-coach-001',
        bio: 'Salsa and bachata instructor.',
        specialties: ['salsa', 'bachata'],
        hireDate: Math.floor(Date.now() / 1000) - 86400 * 400,
      },
    ],
    files: [
      {
        _id: 'demo-file-001',
        ownerType: 'class',
        ownerId: 'demo-class-001',
        classId: 'demo-class-001',
        name: 'Salsa basics.pdf',
        mimeType: 'application/pdf',
        size: 24000,
        uploadedBy: 'demo-coach-001',
        createdAt: Math.floor(Date.now() / 1000) - 86400,
      },
    ],
    classes: cloneDemoData(DEMO_CLASSES),
    locations: cloneDemoData(DEMO_LOCATIONS),
    enrollments: cloneDemoData(DEMO_ENROLLMENTS),
    sessions: buildDemoSessionsForToday(),
    attendance: cloneDemoData(DEMO_ATTENDANCE),
    leaveRequests: cloneDemoData(DEMO_LEAVE_REQUESTS),
    events: cloneDemoData(DEMO_EVENTS),
    eventRegs: cloneDemoData(DEMO_EVENT_REGS),
    notifications: cloneDemoData(DEMO_NOTIFICATIONS),
    announcements: cloneDemoData(DEMO_ANNOUNCEMENTS),
    progressNotes: [],
    invoices: cloneDemoData(DEMO_INVOICES),
    ledger: cloneDemoData(DEMO_LEDGER),
    agreements: cloneDemoData(DEMO_AGREEMENTS),
    pricing: { sessionsPerPaymentUnit: 4, pricePerUnit: 1_200_000, currency: 'IRT' },
    settings: {
      overduePolicy: 'notify_only',
      gracePeriodDays: 7,
      enabledMethods: ['cash', 'bank_transfer', 'manual'],
      requirePrepayOnEnrollment: true,
    },
    leavePolicy: { ...DEFAULT_LEAVE_POLICY },
    registrationTelegram: '@dancemance_admin',
    registrationOpen: true,
    discounts: [
      {
        _id: 'demo-disc-001',
        title: 'تخفیف هنرجویان قبلی سالسا',
        name: 'تخفیف هنرجویان قبلی سالسا',
        targetType: 'class',
        classId: 'demo-class-001',
        appliesTo: 'older_students',
        discountType: 'percentage',
        value: 15,
        active: true,
        createdAt: Math.floor(Date.now() / 1000) - 86400 * 7,
        updatedAt: Math.floor(Date.now() / 1000) - 86400 * 7,
      },
      {
        _id: 'demo-disc-002',
        title: 'تخفیف ویژه ورکشاپ برای مربیان',
        name: 'تخفیف ویژه ورکشاپ برای مربیان',
        targetType: 'event',
        eventId: 'demo-event-001',
        appliesTo: 'role',
        role: 'coach',
        discountType: 'fixed',
        value: 100000,
        active: true,
        createdAt: Math.floor(Date.now() / 1000) - 86400 * 5,
        updatedAt: Math.floor(Date.now() / 1000) - 86400 * 5,
      },
    ],
    shareConfigs: [
      {
        _id: 'demo-share-001',
        operatorPercentage: 40,
        institutePercentage: 60,
        effectiveFrom: Math.floor(Date.now() / 1000) - 86400 * 30,
        createdBy: 'demo-admin-001',
        createdAt: Math.floor(Date.now() / 1000) - 86400 * 30,
      },
    ],
  };
}

function loadState(): DemoState {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw) as DemoState;
      if (parsed.version === STORAGE_VERSION) {
        parsed.sessions = normalizeSessionsForToday(parsed.sessions);
        parsed.leaveRequests = parsed.leaveRequests ?? [];
        parsed.announcements = parsed.announcements ?? [];
        parsed.leavePolicy = normalizeLeavePolicy(parsed.leavePolicy);
        parsed.registrationTelegram = parsed.registrationTelegram ?? '@dancemance_admin';
        if (parsed.registrationOpen === undefined) parsed.registrationOpen = true;
        if (!parsed.discounts) parsed.discounts = [];
        parsed.locations = parsed.locations?.length ? parsed.locations : cloneDemoData(DEMO_LOCATIONS);
        return parsed;
      }
    }
  } catch {
    sessionStorage.removeItem(STORAGE_KEY);
  }
  return freshState();
}

function saveState(state: DemoState) {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ ...state, version: STORAGE_VERSION }));
}

function uid(prefix: string) {
  return `${prefix}-${Date.now().toString(36)}`;
}

function personVisual(id: string) {
  const user = state.people.find((u) => u._id === id) ?? DEMO_USERS.find((u) => u._id === id);
  if (!user) return undefined;
  return {
    _id: user._id,
    name: user.name,
    honorific: user.honorific,
    photoFileId: user.photoFileId,
    photoUrl: user.photoUrl,
  };
}

function personName(id: string) {
  return personVisual(id)?.name;
}

function locationName(id?: string | null) {
  if (!id) return undefined;
  return state.locations?.find((row) => row._id === id)?.name;
}

function normalizeLocationInput(payload: BranchLocationInput) {
  const name = payload.name.trim();
  if (!name) throw new Error('Location name is required');
  const address = payload.address?.trim() || undefined;
  if (payload.latitude === null && payload.longitude === null) {
    return { name, address, latitude: undefined, longitude: undefined, clearCoords: true as const };
  }
  const hasLat = payload.latitude != null && Number.isFinite(payload.latitude);
  const hasLng = payload.longitude != null && Number.isFinite(payload.longitude);
  if (hasLat !== hasLng) throw new Error('Latitude and longitude must be provided together');
  return {
    name,
    address,
    latitude: hasLat ? (payload.latitude as number) : undefined,
    longitude: hasLng ? (payload.longitude as number) : undefined,
    clearCoords: false as const,
  };
}

function attachClassBranchLocation(cls: ClassOffering): ClassOffering {
  if (!cls.branchLocationId) return cls;
  const loc = state.locations.find((row) => row._id === cls.branchLocationId);
  return loc ? { ...cls, branchLocation: { ...loc } } : cls;
}

function attachClassStaffAndLocation(cls: ClassOffering): ClassOffering {
  const withBranch = attachClassBranchLocation(cls);
  const cIds = coachIdsOf(cls);
  const aIds = assistantIdsOf(cls);

  const coaches = cIds.map(personVisual).filter(Boolean) as StaffVisual[];
  const assistants = aIds.map(personVisual).filter(Boolean) as StaffVisual[];

  return {
    ...withBranch,
    coaches,
    assistants,
    coachNames: coaches.map((c) => c.name),
    assistantNames: assistants.map((a) => a.name),
  };
}

function hydrateEvent(event: InstituteEvent): InstituteEvent {
  return {
    ...event,
    location: event.location || locationName(event.branchLocationId),
    responsibleName: event.responsibleName || (event.responsibleUserId ? personName(event.responsibleUserId) : undefined),
  };
}

function locationInUse(locationId: string) {
  return (
    state.classes.some((c) => c.branchLocationId === locationId) ||
    state.sessions.some((s) => s.branchLocationId === locationId) ||
    state.events.some((e) => e.branchLocationId === locationId)
  );
}

function sessionVisibleToCoach(session: ClassSession, coachId: string) {
  if (
    session.coachAssignment.scheduledCoachId === coachId ||
    session.coachAssignment.actualCoachId === coachId
  ) {
    return true;
  }
  const cls = state.classes.find((c) => c._id === session.classId);
  return cls ? classHasStaff(cls, coachId) : false;
}

let state = loadState();

export function resetDemoState() {
  sessionStorage.removeItem(STORAGE_KEY);
  state = freshState();
}

export function bootstrapDemoAuth(): User {
  if (!localStorage.getItem('accessToken')) {
    localStorage.setItem('accessToken', 'demo-token');
    localStorage.setItem('demoUserEmail', 'admin@dancemance.com');
  }
  const email = localStorage.getItem('demoUserEmail') ?? 'admin@dancemance.com';
  return DEMO_USERS.find((u) => u.email === email) ?? DEMO_USERS[0];
}

export const demoAuth = {
  signIn(identifier: string): { accessToken: string } {
    localStorage.setItem('accessToken', 'demo-token');
    const trimmed = identifier.trim().toLowerCase();
    const user = state.people.find(
      (u) => (u.email && u.email.toLowerCase() === trimmed) || (u.phone && u.phone === trimmed),
    );
    localStorage.setItem('demoUserEmail', (user ? user.email || user.phone || user._id : trimmed) || 'demo-user');
    return { accessToken: 'demo-token' };
  },

  signUp(payload: { name: string; email: string; phone?: string; honorific?: User['honorific']; dateOfBirth?: number }): { accessToken: string } {
    const trimmed = payload.email.trim().toLowerCase();
    const existing = state.people.find(
      (u) => (u.email && u.email.toLowerCase() === trimmed) || (payload.phone && u.phone === payload.phone),
    );
    if (existing) throw new Error('این حساب قبلاً ثبت شده است. وارد شوید');
    const user: User = {
      _id: uid('demo-user'),
      email: payload.email,
      name: payload.name.trim() || 'هنرجو',
      role: 'student',
      status: 'active',
      phone: payload.phone,
      honorific: payload.honorific,
      packIcon: 'dancer',
    };
    state.people.push(user);
    state.studentProfiles.push({ userId: user._id, dateOfBirth: payload.dateOfBirth });
    saveState(state);
    localStorage.setItem('accessToken', 'demo-token');
    localStorage.setItem('demoUserEmail', user.email || user.phone || user._id);
    return { accessToken: 'demo-token' };
  },

  requestOtp(phone: string, purpose: OtpPurpose): OtpRequestResult {
    const digits = phone.replace(/[^\d]/g, '');
    const normalized = digits.startsWith('0') ? digits : `0${digits.slice(-10)}`;
    const existing = state.people.find((p) => p.phone === normalized);
    if (purpose === 'signup' && existing) throw new Error('این شماره قبلاً ثبت شده. وارد شوید');
    if ((purpose === 'signin' || purpose === 'reset_password') && !existing) throw new Error('حسابی با این شماره نیست. ثبت‌نام کنید');
    return { phone: normalized, ttlSeconds: 120, resendSeconds: 15, debugCode: '12345' };
  },

  forgotPassword(_identifier: string): { message: string } {
    return { message: 'لینک بازیابی رمز عبور با موفقیت ارسال شد.' };
  },

  resetPasswordWithToken(_token: string, _password: string): { message: string } {
    return { message: 'کلمه عبور با موفقیت به‌روزرسانی شد.' };
  },

  resetPasswordWithOtp(phone: string, code: string, _password: string): { message: string } {
    if (code.replace(/\D/g, '') !== '12345') throw new Error('کد نادرست است');
    const digits = phone.replace(/[^\d]/g, '');
    const normalized = digits.startsWith('0') ? digits : `0${digits.slice(-10)}`;
    const existing = state.people.find((p) => p.phone === normalized);
    if (!existing) throw new Error('حسابی با این شماره نیست');
    return { message: 'کلمه عبور با موفقیت به‌روزرسانی شد.' };
  },

  verifyOtp(payload: { phone: string; code: string; purpose: OtpPurpose; name?: string; honorific?: User['honorific']; dateOfBirth?: number }): { accessToken: string } {
    if (payload.code.replace(/\D/g, '') !== '12345') throw new Error('کد نادرست است');
    const phone = payload.phone;
    let user = state.people.find((p) => p.phone === phone);
    if (payload.purpose === 'signup') {
      if (user) throw new Error('این شماره قبلاً ثبت شده. وارد شوید');
      user = {
        _id: uid('demo-user'),
        email: '',
        name: payload.name?.trim() || 'هنرجو',
        role: 'student',
        status: 'active',
        phone,
        honorific: payload.honorific,
        packIcon: 'dancer',
      };
      state.people.push(user);
      state.studentProfiles.push({ userId: user._id, dateOfBirth: payload.dateOfBirth });
      saveState(state);
    }
    if (!user) throw new Error('حسابی با این شماره نیست');
    localStorage.setItem('accessToken', 'demo-token');
    localStorage.setItem('demoUserEmail', user.email || user.phone || user._id);
    return { accessToken: 'demo-token' };
  },

  getMe(): User {
    const identifier = localStorage.getItem('demoUserEmail') ?? DEMO_USERS[0].email;
    return (
      state.people.find((u) => u.email === identifier || u.phone === identifier || u._id === identifier) ??
      DEMO_USERS[0]
    );
  },

  switchRole(role: UserRole): User {
    const me = this.getMe();
    const user = state.people.find((u) => u._id === me._id);
    if (!user) throw new Error('کاربر یافت نشد');
    const allowed = user.roles && user.roles.length > 0 ? user.roles : [user.role];
    if (!allowed.includes(role) && user.role !== 'owner') {
      throw new Error('شما دسترسی به این نقش را ندارید');
    }
    user.role = role;
    if (!user.roles) user.roles = allowed;
    if (!user.roles.includes(role)) user.roles.push(role);
    if (role === 'student' && !state.studentProfiles.some((p) => p.userId === user._id)) {
      state.studentProfiles.push({ userId: user._id, danceLevel: 'beginner' });
    }
    saveState(state);
    return user;
  },

  listStudents(): User[] {
    return state.people.filter((u) => u.role === 'student');
  },

  listCoaches(): User[] {
    return state.people.filter((u) => u.role === 'coach');
  },

  listPeople(role?: UserRole, opts?: ListPeopleOpts): User[] {
    const rows = this.filterPeopleRows(role, opts);
    if (opts?.limit && opts.limit > 0) return rows.slice(0, opts.limit);
    return rows;
  },

  listPeoplePage(role: UserRole, opts: ListPeopleOpts & { page: number }): PeoplePageResult {
    const rows = this.filterPeopleRows(role, opts);
    const pageSize = opts.pageSize && opts.pageSize > 0 ? Math.min(opts.pageSize, 100) : 24;
    const page = opts.page > 0 ? opts.page : 1;
    const start = (page - 1) * pageSize;
    return {
      items: rows.slice(start, start + pageSize),
      total: rows.length,
      page,
      pageSize,
    };
  },

  filterPeopleRows(role?: UserRole, opts?: ListPeopleOpts): User[] {
    const me = this.getMe();
    let rows: User[];
    if (me.role === 'coach') {
      if (role && role !== 'student') return [];
      const myClassIds = new Set(
        state.classes.filter((c) => classHasStaff(c, me._id)).map((c) => c._id),
      );
      const studentIds = new Set(
        state.enrollments
          .filter((e) => myClassIds.has(e.classId) && ['pending', 'active', 'paused'].includes(e.status))
          .map((e) => e.studentId),
      );
      rows = state.people.filter((u) => u.role === 'student' && studentIds.has(u._id));
    } else {
      rows = role ? state.people.filter((u) => u.role === role) : state.people;
    }
    if (opts?.status && opts.status !== 'all') {
      rows = rows.filter((person) => person.status === opts.status);
    } else if (opts?.status !== 'all') {
      rows = rows.filter((person) => person.status !== 'banned');
    }
    const rawNeedle = opts?.q?.trim();
    if (rawNeedle) {
      const needles = Array.from(new Set([rawNeedle, fromFaDigits(rawNeedle)].filter(Boolean))).map((v) =>
        v.toLowerCase(),
      );
      rows = rows.filter((person) =>
        needles.some((needle) =>
          [person.name, person.email ?? '', person.phone ?? ''].some((value) => value.toLowerCase().includes(needle)),
        ),
      );
    }
    if (role === 'student' && opts?.danceLevel && opts.danceLevel !== 'all') {
      const level = opts.danceLevel;
      rows = rows.filter((person) => {
        const profile = state.studentProfiles.find((p) => p.userId === person._id);
        return (profile?.danceLevel ?? 'beginner') === level;
      });
    }
    const activeEnrollment = (studentId: string, classId?: string) =>
      state.enrollments.some(
        (e) =>
          e.studentId === studentId &&
          ['pending', 'active', 'paused'].includes(e.status) &&
          (!classId || e.classId === classId),
      );
    if (role === 'student' && opts?.classId && opts.enrollment !== 'unenrolled') {
      rows = rows.filter((person) => activeEnrollment(person._id, opts.classId));
    } else if (role === 'student' && opts?.enrollment === 'enrolled') {
      rows = rows.filter((person) => activeEnrollment(person._id));
    } else if (role === 'student' && opts?.enrollment === 'unenrolled') {
      rows = rows.filter((person) => !activeEnrollment(person._id));
    }
    if (role === 'student') {
      rows = rows.map((person) => {
        const profile = state.studentProfiles.find((p) => p.userId === person._id);
        return profile?.danceLevel ? { ...person, danceLevel: profile.danceLevel } : person;
      });
    }
    return rows.sort((a, b) => a.name.localeCompare(b.name, 'fa'));
  },

  getPerson(userId: string): PersonDetail {
    const me = this.getMe();
    const user = state.people.find((u) => u._id === userId) ?? this.getMe();
    if (me.role === 'student' && user.role === 'student' && me._id !== userId) {
      throw new Error('Cannot view this profile');
    }
    const studentProfile = state.studentProfiles.find((p) => p.userId === userId) ?? null;
    const coachProfile = state.coachProfiles.find((p) => p.userId === userId) ?? null;
    if (isAdminLike(me.role) || !studentProfile) {
      return { user, studentProfile, coachProfile };
    }
    if (me.role === 'coach') {
      const { danceLevel: _level, ...safe } = studentProfile;
      return { user, studentProfile: safe, coachProfile };
    }
    const { danceLevel: _level, notes: _notes, ...safe } = studentProfile;
    return { user, studentProfile: safe, coachProfile };
  },

  createPerson(payload: {
    name: string;
    email: string;
    password: string;
    role: UserRole;
    phone?: string;
    honorific?: User['honorific'];
    contactVerified?: boolean;
  }): User {
    const me = this.getMe();
    if (!assignableRoles(me.role).includes(payload.role)) {
      throw new Error('Insufficient permissions to assign this role');
    }
    const now = Math.floor(Date.now() / 1000);
    const skipVerification = payload.contactVerified !== false;
    const user: User = {
      _id: uid('demo-user'),
      name: payload.name,
      email: payload.email,
      role: payload.role,
      status: 'active',
      phone: payload.phone,
      honorific: payload.honorific,
      contactVerificationRequired: !skipVerification,
      emailVerifiedAt: skipVerification ? now : undefined,
      phoneVerifiedAt: skipVerification && payload.phone ? now : undefined,
    };
    state.people.push(user);
    saveState(state);
    return user;
  },

  requestEmailVerification() {
    const me = this.getMe();
    return { email: me.email ?? '', ttlSeconds: 120, resendSeconds: 90, debugCode: '12345' };
  },

  verifyEmailContact(code: string): User {
    const me = this.getMe();
    if (code.replace(/\D/g, '') !== '12345') throw new Error('کد نادرست است');
    const user = state.people.find((u) => u._id === me._id);
    if (!user) throw new Error('User not found');
    user.emailVerifiedAt = Math.floor(Date.now() / 1000);
    saveState(state);
    return user;
  },

  requestPhoneContactVerification() {
    const me = this.getMe();
    if (!me.phone) throw new Error('شماره موبایل ثبت نشده است');
    return { phone: me.phone, ttlSeconds: 120, resendSeconds: 90, debugCode: '12345' };
  },

  verifyPhoneContact(code: string): User {
    const me = this.getMe();
    if (code.replace(/\D/g, '') !== '12345') throw new Error('کد نادرست است');
    const user = state.people.find((u) => u._id === me._id);
    if (!user) throw new Error('User not found');
    user.phoneVerifiedAt = Math.floor(Date.now() / 1000);
    saveState(state);
    return user;
  },

  updatePerson(
    userId: string,
    payload: Partial<Pick<User, 'name' | 'email' | 'phone' | 'role' | 'status' | 'honorific' | 'packIcon' | 'photoFileId' | 'photoUrl'>>,
  ): User {
    const me = this.getMe();
    const user = state.people.find((u) => u._id === userId);
    if (!user) throw new Error('User not found');
    if (user.role === 'owner' && userId !== me._id) {
      throw new Error('Owner accounts cannot be modified');
    }
    if (payload.role && payload.role !== user.role) {
      if (!assignableRoles(me.role).includes(payload.role)) {
        throw new Error('Insufficient permissions to assign this role');
      }
      if (user.role === 'admin' && !isOwner(me.role)) {
        throw new Error('Only the owner can change admin roles');
      }
    }
    if (user.role === 'admin' && payload.status && payload.status !== 'active' && !isOwner(me.role)) {
      throw new Error('Only the owner can deactivate or ban admins');
    }
    Object.assign(user, payload);
    saveState(state);
    return user;
  },

  async uploadPhoto(file: File, userId?: string): Promise<User> {
    const target = !userId || userId === 'me' ? this.getMe()._id : userId;
    const photoUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(new Error('read'));
      reader.readAsDataURL(file);
    });
    return this.updatePerson(target, { photoUrl, photoFileId: `demo-photo-${target}` });
  },

  deletePerson(userId: string): { deleted: boolean; userId: string; role: UserRole } {
    const me = this.getMe();
    if (!isOwner(me.role)) throw new Error('Only the owner can delete users');
    if (userId === me._id) throw new Error('You cannot delete your own account');
    const idx = state.people.findIndex((u) => u._id === userId);
    if (idx < 0) throw new Error('User not found');
    const [removed] = state.people.splice(idx, 1);
    if (removed.role === 'owner') {
      state.people.splice(idx, 0, removed);
      throw new Error('Owner accounts cannot be deleted');
    }
    state.studentProfiles = state.studentProfiles.filter((p) => p.userId !== userId);
    state.coachProfiles = state.coachProfiles.filter((p) => p.userId !== userId);
    saveState(state);
    return { deleted: true, userId, role: removed.role };
  },

  updateStudentProfile(userId: string, payload: Partial<StudentProfile>): StudentProfile {
    let row = state.studentProfiles.find((p) => p.userId === userId);
    if (!row) {
      row = { userId, ...payload };
      state.studentProfiles.push(row);
    } else {
      Object.assign(row, payload);
    }
    saveState(state);
    return row;
  },

  updateCoachProfile(userId: string, payload: Partial<CoachProfile>): CoachProfile {
    let row = state.coachProfiles.find((p) => p.userId === userId);
    if (!row) {
      row = { userId, specialties: [], ...payload };
      state.coachProfiles.push(row);
    } else {
      Object.assign(row, payload);
    }
    saveState(state);
    return row;
  },

  logout() {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('demoUserEmail');
  },

  isAuthenticated() {
    return !!localStorage.getItem('accessToken');
  },
};

function normalizeClassName(name: string): string {
  return name
    .toLowerCase()
    .replace(/[\u200B-\u200D\uFEFF]/g, '')
    .replace(/ي/g, 'ی')
    .replace(/ك/g, 'ک')
    .replace(/[^a-z0-9\u0600-\u06FF]/gi, '');
}

function levenshteinDistance(a: string, b: string): number {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        dp[i][j] = dp[i - 1][j - 1];
      } else {
        dp[i][j] = 1 + Math.min(dp[i - 1][j], dp[i][j - 1], dp[i - 1][j - 1]);
      }
    }
  }
  return dp[m][n];
}

function areClassNamesSimilar(name1: string, name2: string): boolean {
  const n1 = normalizeClassName(name1);
  const n2 = normalizeClassName(name2);
  if (!n1 || !n2) return false;
  if (n1 === n2) return true;
  if (n1.includes(n2) || n2.includes(n1)) {
    const minLen = Math.min(n1.length, n2.length);
    const maxLen = Math.max(n1.length, n2.length);
    if (minLen / maxLen >= 0.8) return true;
  }
  const maxLen = Math.max(n1.length, n2.length);
  if (maxLen >= 5 && levenshteinDistance(n1, n2) <= 2) return true;
  return false;
}

export const demoAcademic = {
  listClasses(): ClassOffering[] {
    const active = state.classes.filter((c) => c.status === 'active');
    const me = demoAuth.getMe();
    let rows = active;
    if (me.role === 'coach') rows = active.filter((c) => classHasStaff(c, me._id));
    else if (me.role === 'student') {
      const allowed = new Set(
        state.enrollments
          .filter((e) => e.studentId === me._id && ['pending', 'active', 'paused'].includes(e.status))
          .map((e) => e.classId),
      );
      rows = active.filter((c) => allowed.has(c._id));
    }
    return rows.map(attachClassStaffAndLocation);
  },

  createClass(payload: CreateClassPayload): ClassOffering {
    if (payload.schedule?.dayOfWeek !== undefined && payload.schedule?.timeOfDay) {
      const conflict = state.classes.find((c) => {
        if (c.status !== 'active') return false;
        if (c.schedule?.dayOfWeek !== payload.schedule?.dayOfWeek || c.schedule?.timeOfDay !== payload.schedule?.timeOfDay) return false;
        const sameLoc =
          (payload.branchLocationId && c.branchLocationId === payload.branchLocationId) ||
          (!payload.branchLocationId && !c.branchLocationId);
        return sameLoc && areClassNamesSimilar(payload.name, c.name);
      });
      if (conflict) {
        throw new Error('کلاسی فعال با نام مشابه در همین زمان و مکان از قبل وجود دارد.');
      }
    }

    const now = Math.floor(Date.now() / 1000);
    const coachIds = payload.coachIds?.length ? payload.coachIds : [payload.coachId];
    const cls: ClassOffering = {
      _id: uid('demo-class'),
      name: payload.name,
      danceSportType: payload.danceSportType,
      level: payload.level ?? 'open',
      description: payload.description,
      defaultDurationMinutes: payload.defaultDurationMinutes ?? 60,
      coachId: coachIds[0] ?? payload.coachId,
      coachIds,
      assistantIds: payload.assistantIds ?? [],
      schedule: payload.schedule,
      coachCompensations: payload.coachCompensations,
      pricing: payload.pricing,
      registrationOpen: payload.registrationOpen ?? true,
      telegramGroupUrl: payload.telegramGroupUrl,
      branchLocationId: payload.branchLocationId || undefined,
      location: locationName(payload.branchLocationId),
      status: 'active',
      createdAt: now,
      updatedAt: now,
    };
    state.classes.push(cls);
    saveState(state);
    return attachClassStaffAndLocation(cls);
  },

  deleteClass(classId: string): { success: boolean; classId: string } {
    const idx = state.classes.findIndex((c) => c._id === classId);
    if (idx >= 0) {
      state.classes.splice(idx, 1);
    }
    state.sessions = state.sessions.filter((s) => s.classId !== classId);
    state.enrollments = state.enrollments.filter((e) => e.classId !== classId);
    saveState(state);
    return { success: true, classId };
  },

  listOpenClasses(): ClassOffering[] {
    const classes = state.classes.filter((c) => c.status === 'active' && c.registrationOpen);
    const currentUser = demoAuth.getMe();
    const accessible = (() => {
      if (!currentUser || currentUser.role !== 'student') {
        return classes;
      }
      const profile = state.studentProfiles.find((p) => p.userId === currentUser._id);
      return classes.filter((cls) => {
        const classLevel = cls.level ?? 'open';
        if (classLevel === 'open') return true;
        const studentLevel =
          profile?.danceLevels?.[cls.danceSportType] ||
          profile?.danceLevel ||
          'beginner';
        return isClassLevelAccessible(studentLevel, classLevel);
      });
    })();
    return accessible.map((cls) => {
      const copy = { ...attachClassStaffAndLocation(cls) };
      delete copy.telegramGroupUrl;
      return copy;
    });
  },


  getRegistrationStatus(): { registrationOpen: boolean } {
    return { registrationOpen: state.registrationOpen ?? true };
  },

  updateRegistrationStatus(registrationOpen: boolean): { registrationOpen: boolean } {
    state.registrationOpen = registrationOpen;
    saveState(state);
    return { registrationOpen };
  },

  getInstituteSettings(): InstituteSettings {
    return {
      registrationOpen: state.registrationOpen ?? true,
      registrationTelegram: state.registrationTelegram ?? '',
    };
  },

  updateInstituteSettings(payload: Partial<InstituteSettings>): InstituteSettings {
    if (payload.registrationOpen !== undefined) {
      state.registrationOpen = payload.registrationOpen;
    }
    if (payload.registrationTelegram !== undefined) {
      state.registrationTelegram = payload.registrationTelegram;
    }
    saveState(state);
    return this.getInstituteSettings();
  },

  getInstituteContact(): InstituteContact {
    return {
      registrationTelegram: state.registrationTelegram ?? '',
      registrationOpen: state.registrationOpen ?? true,
    };
  },

  updateInstituteContact(payload: Partial<InstituteContact>): InstituteContact {
    if (payload.registrationTelegram !== undefined) {
      state.registrationTelegram = payload.registrationTelegram;
      saveState(state);
    }
    return this.getInstituteContact();
  },

  listDiscounts(params?: { targetType?: DiscountTargetType; classId?: string; eventId?: string }): RegistrationDiscount[] {
    let result = [...(state.discounts || [])];
    if (params?.targetType) {
      result = result.filter((d) => d.targetType === params.targetType);
    }
    if (params?.classId) {
      result = result.filter((d) => d.classId === params.classId);
    }
    if (params?.eventId) {
      result = result.filter((d) => d.eventId === params.eventId);
    }
    return result;
  },

  createDiscount(payload: CreateDiscountPayload): RegistrationDiscount {
    const now = Math.floor(Date.now() / 1000);
    const row: RegistrationDiscount = {
      _id: uid('demo-disc'),
      title: payload.title || payload.name || '',
      name: payload.name || payload.title || '',
      targetType: payload.targetType,
      classId: payload.classId,
      eventId: payload.eventId,
      appliesTo: payload.appliesTo,
      studentId: payload.studentId,
      role: payload.role,
      discountType: payload.discountType,
      value: payload.value,
      active: payload.active ?? true,
      createdAt: now,
      updatedAt: now,
    };
    state.discounts.push(row);
    saveState(state);
    return row;
  },

  updateDiscount(id: string, payload: Partial<CreateDiscountPayload>): RegistrationDiscount {
    const row = state.discounts.find((d) => d._id === id);
    if (!row) throw new Error('Discount not found');
    Object.assign(row, payload, { updatedAt: Math.floor(Date.now() / 1000) });
    saveState(state);
    return row;
  },

  deleteDiscount(id: string): { success: boolean } {
    state.discounts = state.discounts.filter((d) => d._id !== id);
    saveState(state);
    return { success: true };
  },

  resolveDiscount(params: {
    targetType: DiscountTargetType;
    targetId: string;
    studentId?: string;
    role?: string;
  }): {
    discountAmount: number;
    discountPercent?: number;
    discountReason: string;
    discountId?: string;
  } {
    let basePrice = 0;
    if (params.targetType === 'class') {
      const cls = state.classes.find((c) => c._id === params.targetId);
      basePrice = cls?.pricing?.pricePerUnit ?? state.pricing.pricePerUnit;
    } else {
      const ev = state.events.find((e) => e._id === params.targetId);
      basePrice = ev?.price ?? 0;
    }

    if (basePrice <= 0) {
      return { discountAmount: 0, discountReason: '' };
    }

    const relevant = (state.discounts || []).filter(
      (d) =>
        d.active &&
        d.targetType === params.targetType &&
        (params.targetType === 'class' ? d.classId === params.targetId : d.eventId === params.targetId),
    );

    let isOlderStudent = false;
    if (params.studentId && params.targetType === 'class') {
      isOlderStudent = state.enrollments.some(
        (e) => e.classId === params.targetId && e.studentId === params.studentId,
      );
    }

    let bestDiscountAmount = 0;
    let bestPercent: number | undefined;
    let bestReason = '';
    let bestId: string | undefined;

    for (const d of relevant) {
      let applies = false;
      if (d.appliesTo === 'all') applies = true;
      else if (d.appliesTo === 'specific_student' && d.studentId === params.studentId) applies = true;
      else if (d.appliesTo === 'older_students' && isOlderStudent) applies = true;
      else if (d.appliesTo === 'role' && d.role === params.role) applies = true;

      if (!applies) continue;

      let amount = 0;
      let pct: number | undefined;
      if (d.discountType === 'percentage') {
        pct = d.value;
        amount = Math.round((basePrice * d.value) / 100);
      } else {
        amount = Math.min(d.value, basePrice);
      }

      if (amount > bestDiscountAmount) {
        bestDiscountAmount = amount;
        bestPercent = pct;
        bestReason = d.title || d.name || (d.appliesTo === 'older_students' ? 'تخفیف هنرجوی قبلی' : 'تخفیف ویژه');
        bestId = d._id;
      }
    }

    return {
      discountAmount: bestDiscountAmount,
      discountPercent: bestPercent,
      discountReason: bestReason,
      discountId: bestId,
    };
  },

  listBranchLocations(): BranchLocation[] {
    return [...state.locations].sort((a, b) => a.name.localeCompare(b.name, 'fa'));
  },

  createBranchLocation(payload: BranchLocationInput): BranchLocation {
    const next = normalizeLocationInput(payload);
    const key = next.name.toLowerCase();
    if (state.locations.some((row) => row.name.toLowerCase() === key)) {
      throw new Error('Location already exists');
    }
    const now = Math.floor(Date.now() / 1000);
    const row: BranchLocation = {
      _id: uid('demo-loc'),
      name: next.name,
      address: next.address,
      latitude: next.latitude,
      longitude: next.longitude,
      createdAt: now,
      updatedAt: now,
    };
    state.locations.push(row);
    saveState(state);
    return row;
  },

  updateBranchLocation(locationId: string, payload: BranchLocationInput): BranchLocation {
    const row = state.locations.find((item) => item._id === locationId);
    if (!row) throw new Error('Location not found');
    const next = normalizeLocationInput(payload);
    if (
      state.locations.some(
        (item) => item._id !== locationId && item.name.toLowerCase() === next.name.toLowerCase(),
      )
    ) {
      throw new Error('Location already exists');
    }
    row.name = next.name;
    row.address = next.address;
    if (next.clearCoords) {
      delete row.latitude;
      delete row.longitude;
    } else {
      row.latitude = next.latitude;
      row.longitude = next.longitude;
    }
    row.updatedAt = Math.floor(Date.now() / 1000);
    for (const cls of state.classes) {
      if (cls.branchLocationId === locationId) cls.location = next.name;
    }
    for (const session of state.sessions) {
      if (session.branchLocationId === locationId) session.location = next.name;
    }
    for (const event of state.events) {
      if (event.branchLocationId === locationId) event.location = next.name;
    }
    saveState(state);
    return row;
  },

  deleteBranchLocation(locationId: string) {
    if (locationInUse(locationId)) throw new Error('Location is in use');
    const next = state.locations.filter((row) => row._id !== locationId);
    if (next.length === state.locations.length) throw new Error('Location not found');
    state.locations = next;
    saveState(state);
  },

  getClass(classId: string): ClassOffering {
    const cls = state.classes.find((c) => c._id === classId);
    if (!cls) throw new Error('Class not found');
    const me = demoAuth.getMe();
    if (me.role === 'coach') {
      const covers =
        classHasStaff(cls, me._id) ||
        state.sessions.some(
          (s) =>
            s.classId === classId &&
            (s.coachAssignment.scheduledCoachId === me._id || s.coachAssignment.actualCoachId === me._id),
        );
      if (!covers) throw new Error('Forbidden');
    }
    if (me.role === 'student') {
      const allowed = state.enrollments.some(
        (e) =>
          e.classId === classId &&
          e.studentId === me._id &&
          ['pending', 'active', 'paused'].includes(e.status),
      );
      if (!allowed && !cls.registrationOpen) throw new Error('Forbidden');
    }

    const canSeeTelegramGroup = (() => {
      if (['admin', 'owner', 'super_admin'].includes(me.role)) return true;
      if (me.role === 'coach') {
        return (
          classHasStaff(cls, me._id) ||
          state.sessions.some(
            (s) =>
              s.classId === classId &&
              (s.coachAssignment.scheduledCoachId === me._id || s.coachAssignment.actualCoachId === me._id),
          )
        );
      }
      if (me.role === 'student') {
        return state.enrollments.some(
          (e) =>
            e.classId === classId &&
            e.studentId === me._id &&
            ['pending', 'active', 'paused'].includes(e.status),
        );
      }
      return false;
    })();

    const withBranch = attachClassStaffAndLocation(cls);
    if (!canSeeTelegramGroup) {
      const copy = { ...withBranch };
      delete copy.telegramGroupUrl;
      return copy;
    }
    return withBranch;
  },


  updateClass(classId: string, payload: Partial<CreateClassPayload & { status: string }>): ClassOffering {
    const cls = state.classes.find((c) => c._id === classId);
    if (!cls) throw new Error('Class not found');
    const prospectiveName = payload.name ?? cls.name;
    const prospectiveSchedule = payload.schedule !== undefined ? payload.schedule : cls.schedule;
    const prospectiveBranch = payload.branchLocationId !== undefined ? payload.branchLocationId : cls.branchLocationId;
    const prospectiveStatus = payload.status ?? cls.status;

    if (prospectiveStatus === 'active' && prospectiveSchedule?.dayOfWeek !== undefined && prospectiveSchedule?.timeOfDay) {
      const conflict = state.classes.find((c) => {
        if (c._id === classId || c.status !== 'active') return false;
        if (c.schedule?.dayOfWeek !== prospectiveSchedule.dayOfWeek || c.schedule?.timeOfDay !== prospectiveSchedule.timeOfDay) return false;
        const sameLoc =
          (prospectiveBranch && c.branchLocationId === prospectiveBranch) ||
          (!prospectiveBranch && !c.branchLocationId);
        return sameLoc && areClassNamesSimilar(prospectiveName, c.name);
      });
      if (conflict) {
        throw new Error('کلاسی فعال با نام مشابه در همین زمان و مکان از قبل وجود دارد.');
      }
    }

    const coachIds = payload.coachIds?.length ? payload.coachIds : coachIdsOf({ ...cls, ...payload });
    Object.assign(cls, payload, {
      coachIds,
      coachId: coachIds[0] ?? cls.coachId,
      assistantIds: payload.assistantIds ?? cls.assistantIds ?? [],
      branchLocationId: payload.branchLocationId || undefined,
      location: payload.branchLocationId === undefined ? cls.location : locationName(payload.branchLocationId),
      updatedAt: Math.floor(Date.now() / 1000),
    });
    saveState(state);
    return cls;
  },

  listMyEnrollments(userId: string): Enrollment[] {
    return state.enrollments.filter((e) => e.studentId === userId);
  },

  listClassEnrollments(classId: string): Enrollment[] {
    return state.enrollments.filter((e) => e.classId === classId && e.status === 'active');
  },

  listStudentEnrollments(studentId: string): Enrollment[] {
    return state.enrollments.filter((e) => e.studentId === studentId);
  },

  buildClassRoster(classId: string): RosterRow[] {
    return state.enrollments
      .filter((e) => e.classId === classId && ['pending', 'active', 'paused'].includes(e.status))
      .map((enrollment) => {
        const student = state.people.find((u) => u._id === enrollment.studentId);
        return {
          enrollment,
          student: student
            ? {
                _id: student._id,
                name: student.name,
                email: student.email,
                phone: student.phone,
                status: student.status,
                honorific: student.honorific,
                photoFileId: student.photoFileId,
                photoUrl: student.photoUrl,
              }
            : { _id: enrollment.studentId, name: 'Unknown student' },
        };
      });
  },

  listClassRoster(classId: string): RosterRow[] {
    if (demoAuth.getMe().role === 'student') {
      throw new Error('Forbidden');
    }
    return this.buildClassRoster(classId);
  },

  createEnrollment(
    classId: string,
    studentId: string,
    entitlementGranted = 4,
    leavePolicy?: { minNoticeDays: number; maxDurationDays: number },
    discount?: EnrollmentDiscount,
  ): Enrollment {
    const now = Math.floor(Date.now() / 1000);
    const policy = {
      minNoticeDays: leavePolicy?.minNoticeDays ?? state.leavePolicy.minNoticeDays,
      maxDurationDays: leavePolicy?.maxDurationDays ?? state.leavePolicy.maxDurationDays,
    };
    const cls = state.classes.find((c) => c._id === classId);
    if (cls && cls.level && cls.level !== 'open') {
      const profile = state.studentProfiles.find((p) => p.userId === studentId);
      const studentLevel =
        profile?.danceLevels?.[cls.danceSportType] ||
        profile?.danceLevel ||
        'beginner';
      if (!isClassLevelAccessible(studentLevel, cls.level)) {
        throw new Error(
          `سطح مهارت هنرجو برای این کلاس (${cls.level}) کافی نیست. سطح فعلی: ${studentLevel}`,
        );
      }
    }

    const existing = state.enrollments.find((e) => e.classId === classId && e.studentId === studentId);
    if (existing) {
      if (['pending', 'active', 'paused'].includes(existing.status)) {
        throw new Error('Already enrolled');
      }
      existing.status = 'active';
      existing.startDate = now;
      existing.endDate = undefined;
      existing.entitlement = { granted: entitlementGranted, consumed: 0, remaining: entitlementGranted };
      existing.leavePolicy = policy;
      if (discount) existing.discount = discount;
      existing.updatedAt = now;
      saveState(state);
      return existing;
    }
    const enrollment: Enrollment = {
      _id: uid('demo-enroll'),
      studentId,
      classId,
      status: 'active',
      enrolledAt: now,
      startDate: now,
      entitlement: { granted: entitlementGranted, consumed: 0, remaining: entitlementGranted },
      leavePolicy: policy,
      discount: discount || undefined,
      createdAt: now,
      updatedAt: now,
    };
    state.enrollments.push(enrollment);
    saveState(state);
    return enrollment;
  },

  updateEnrollmentStatus(enrollmentId: string, status: Enrollment['status']): Enrollment {
    const enrollment = state.enrollments.find((e) => e._id === enrollmentId);
    if (!enrollment) throw new Error('Enrollment not found');
    enrollment.status = status;
    enrollment.updatedAt = Math.floor(Date.now() / 1000);
    if (status === 'cancelled' || status === 'completed') {
      enrollment.endDate = enrollment.updatedAt;
    }
    saveState(state);
    return enrollment;
  },

  listTodaySessions(coachId?: string): ClassSession[] {
    state.sessions = normalizeSessionsForToday(state.sessions);
    const start = new Date();
    start.setHours(0, 0, 0, 0);
    const dayStart = start.getTime() / 1000;
    const dayEnd = dayStart + 86400;

    return state.sessions.filter((s) => {
      if (s.scheduledStart < dayStart || s.scheduledStart >= dayEnd) return false;
      if (!coachId) return true;
      return sessionVisibleToCoach(s, coachId);
    });
  },

  listSessionsInRange(from: number, to: number, coachId?: string): ClassSession[] {
    state.sessions = normalizeSessionsForToday(state.sessions);
    return state.sessions.filter((s) => {
      if (s.scheduledStart < from || s.scheduledStart > to) return false;
      if (!coachId) return true;
      return sessionVisibleToCoach(s, coachId);
    });
  },

  listClassSessions(classId: string): ClassSession[] {
    const roster = state.enrollments.filter(
      (e) => e.classId === classId && ['pending', 'active', 'paused'].includes(e.status),
    );
    return state.sessions
      .filter((s) => s.classId === classId)
      .map((session) => {
        const marks = state.attendance.filter((a) => a.sessionId === session._id);
        const scheduledCoachId = session.coachAssignment.scheduledCoachId;
        const actualCoachId = session.coachAssignment.actualCoachId ?? scheduledCoachId;
        const assistantCoachId = session.coachAssignment.assistantCoachId;
        const scheduledCoach = personVisual(scheduledCoachId);
        const actualCoach = personVisual(actualCoachId);
        const assistantCoach = assistantCoachId ? personVisual(assistantCoachId) : undefined;
        const coachesList = [
          actualCoach ? { ...actualCoach, role: 'actual' } : null,
          scheduledCoach && actualCoachId !== scheduledCoachId ? { ...scheduledCoach, role: 'scheduled' } : null,
        ].filter(Boolean) as any[];
        const assistantsList = [
          assistantCoach ? { ...assistantCoach, role: 'assistant' } : null,
        ].filter(Boolean) as any[];

        return {
          ...session,
          scheduledCoachName: scheduledCoach?.name,
          actualCoachName: actualCoach?.name,
          assistantCoachName: assistantCoach?.name,
          scheduledCoach,
          actualCoach,
          assistantCoach,
          coaches: coachesList,
          assistants: assistantsList,
          replacement: actualCoachId !== scheduledCoachId,
          rosterSize: roster.length,
          presentCount: marks.filter((m) => m.status === 'present').length,
          absentCount: marks.filter((m) => m.status === 'absent').length,
          leaveCount: marks.filter((m) => m.status === 'on_leave').length,
          unmarkedCount: Math.max(0, roster.length - marks.length),
        };
      });
  },


  createSession(classId: string, payload: CreateSessionPayload): ClassSession {
    const cls = state.classes.find((c) => c._id === classId);
    const duration = payload.durationMinutes ?? cls?.defaultDurationMinutes ?? 60;
    const now = Math.floor(Date.now() / 1000);
    const scheduledCoachId = payload.scheduledCoachId ?? cls?.coachId ?? 'demo-coach-001';
    const coachAssignment: ClassSession['coachAssignment'] = { scheduledCoachId };
    if (payload.actualCoachId && payload.actualCoachId !== scheduledCoachId) {
      coachAssignment.actualCoachId = payload.actualCoachId;
    }
    if (payload.assistantCoachId) {
      coachAssignment.assistantCoachId = payload.assistantCoachId;
    }
    const session: ClassSession = {
      _id: uid('demo-session'),
      classId,
      scheduledStart: payload.scheduledStart,
      scheduledEnd: payload.scheduledStart + duration * 60,
      durationMinutes: duration,
      coachAssignment,
      status: 'scheduled',
      notes: payload.notes,
      branchLocationId: payload.branchLocationId || cls?.branchLocationId,
      location: locationName(payload.branchLocationId || cls?.branchLocationId) ?? cls?.location,
      createdAt: now,
      updatedAt: now,
    };
    state.sessions.push(session);
    saveState(state);
    return session;
  },

  cancelSession(
    sessionId: string,
    payload?: {
      reason?: string;
      source?: 'institute' | 'coach';
      reschedule?: boolean;
      rescheduleOffsetDays?: number;
      substituteCoachId?: string;
      assistantCoachId?: string | null;
    },
  ): { session: ClassSession; rescheduledSession?: ClassSession | null } {
    const session = state.sessions.find((s) => s._id === sessionId);
    if (!session) throw new Error('Session not found');
    session.status = 'cancelled';
    session.cancellation = {
      source: payload?.source ?? 'institute',
      reason: payload?.reason,
      cancelledBy: demoAuth.getMe()._id,
      cancelledAt: Math.floor(Date.now() / 1000),
    };
    session.updatedAt = Math.floor(Date.now() / 1000);
    let rescheduledSession: ClassSession | null = null;
    if (payload?.reschedule !== false) {
      const offsetDays = payload?.rescheduleOffsetDays ?? 7;
      const scheduledCoachId = session.coachAssignment.scheduledCoachId;
      const substituteId = payload?.substituteCoachId?.trim();
      rescheduledSession = this.createSession(session.classId, {
        scheduledStart: session.scheduledStart + offsetDays * 86400,
        durationMinutes: session.durationMinutes,
        scheduledCoachId,
        actualCoachId: substituteId && substituteId !== scheduledCoachId ? substituteId : undefined,
        assistantCoachId: payload?.assistantCoachId ?? undefined,
        branchLocationId: session.branchLocationId ?? null,
      });
    }
    saveState(state);
    return { session, rescheduledSession };
  },

  listSessionAttendance(sessionId: string): AttendanceRecord[] {
    return state.attendance.filter((a) => a.sessionId === sessionId);
  },

  listAttendanceRegister(coachId?: string): AttendanceRegisterRow[] {
    const rows: AttendanceRegisterRow[] = [];
    for (const record of state.attendance) {
      const session = state.sessions.find((s) => s._id === record.sessionId);
      if (!session || session.status === 'cancelled') continue;
      if (coachId && !sessionVisibleToCoach(session, coachId)) continue;
      const enrollment = state.enrollments.find((e) => e._id === record.enrollmentId);
      const cls = enrollment
        ? state.classes.find((c) => c._id === enrollment.classId)
        : state.classes.find((c) => c._id === session.classId);
      const frozen =
        record.status === 'on_leave' ||
        this.hasApprovedLeave(record.enrollmentId, session.scheduledStart);
      rows.push({
        recordId: record._id,
        sessionId: record.sessionId,
        enrollmentId: record.enrollmentId,
        studentId: record.studentId,
        studentName: personName(record.studentId) ?? record.studentId,
        studentHonorific: personVisual(record.studentId)?.honorific,
        studentPhotoFileId: personVisual(record.studentId)?.photoFileId,
        studentPhotoUrl: personVisual(record.studentId)?.photoUrl,
        classId: cls?._id ?? session.classId,
        className: cls?.name ?? '—',
        sessionStart: session.scheduledStart,
        status: record.status,
        frozen,
        scheduledCoachId: session.coachAssignment.scheduledCoachId,
        actualCoachId: session.coachAssignment.actualCoachId,
      });
    }
    return rows.sort((a, b) => b.sessionStart - a.sessionStart);
  },

  getActivityFeed(): ActivityFeedItem[] {
    const items: ActivityFeedItem[] = [];
    const now = Math.floor(Date.now() / 1000);
    const startToday = (() => {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      return Math.floor(d.getTime() / 1000);
    })();

    for (const enrollment of state.enrollments) {
      const cls = state.classes.find((c) => c._id === enrollment.classId);
      items.push({
        id: `enroll-${enrollment._id}`,
        kind: 'enrollment',
        at: enrollment.enrolledAt ?? enrollment.createdAt ?? 0,
        name: personName(enrollment.studentId) ?? enrollment.studentId,
        detail: cls ? `ثبت‌نام در ${cls.name}` : 'ثبت‌نام جدید',
      });
    }

    for (const inv of state.invoices) {
      const student = personName(inv.studentId) ?? inv.studentName ?? inv.studentId;
      if (inv.status === 'paid' && inv.paidAt) {
        items.push({
          id: `inv-paid-${inv._id}`,
          kind: 'invoice_paid',
          at: inv.paidAt,
          name: student,
          detail: `${inv.invoiceNumber} · ${inv.sessionsIncluded} جلسه · پرداخت‌شده`,
        });
      } else if (inv.status === 'due') {
        items.push({
          id: `inv-due-${inv._id}`,
          kind: inv.overdue ? 'invoice_overdue' : 'invoice_sent',
          at: inv.createdAt,
          name: student,
          detail: `${inv.invoiceNumber} · ${inv.sessionsIncluded} جلسه · ${inv.className}`,
        });
      }
    }

    for (const leave of state.leaveRequests) {
      const student = personName(leave.studentId) ?? leave.studentName ?? leave.studentId;
      const cls = leave.className ?? state.classes.find((c) => c._id === leave.classId)?.name ?? '';
      if (leave.status === 'denied') {
        items.push({
          id: `leave-deny-${leave._id}`,
          kind: 'leave_denied',
          at: leave.reviewedAt ?? leave.updatedAt ?? leave.createdAt,
          name: student,
          detail: `${cls} · اطلاع ${leave.noticeDays} روز (حداقل ${leave.policyMinNoticeDays})`,
        });
      }
      if (leave.status === 'approved') {
        items.push({
          id: `leave-ok-${leave._id}`,
          kind: 'leave_approved',
          at: leave.reviewedAt ?? leave.updatedAt ?? leave.createdAt,
          name: student,
          detail: `${cls} · مرخصی تأیید شد`,
        });
        items.push({
          id: `leave-start-${leave._id}`,
          kind: 'leave_starts',
          at: leave.leaveStart,
          name: student,
          detail: `${cls} · بدون مصرف جلسه تا پایان مرخصی`,
          future: leave.leaveStart > now,
        });
      }
    }

    items.push({
      id: 'today',
      kind: 'today',
      at: startToday + 12 * 3600,
      name: '',
      detail: 'شما اینجا هستید',
    });

    return items.sort((a, b) => {
      if (a.kind === 'today' && b.kind !== 'today') return -1;
      if (b.kind === 'today' && a.kind !== 'today') return 1;
      if (Boolean(a.future) !== Boolean(b.future)) return a.future ? 1 : -1;
      return b.at - a.at;
    });
  },

  recordAttendance(sessionId: string, payload: RecordAttendancePayload): AttendanceRecord {
    const now = Math.floor(Date.now() / 1000);
    const me = demoAuth.getMe();
    const session = state.sessions.find((s) => s._id === sessionId);
    if (!session) throw new Error('Session not found');

    if (payload.status === 'on_leave') {
      const eligible = this.hasApprovedLeave(payload.enrollmentId, session.scheduledStart);
      if (!eligible && !isAdminLike(me.role)) {
        throw new Error('Leave can only be marked when an approved leave request covers this session');
      }
    }

    const idx = state.attendance.findIndex(
      (a) => a.sessionId === sessionId && a.enrollmentId === payload.enrollmentId,
    );
    const previous = idx >= 0 ? state.attendance[idx].status : undefined;
    const record: AttendanceRecord = {
      _id: idx >= 0 ? state.attendance[idx]._id : uid('demo-att'),
      sessionId,
      ...payload,
      recordedBy: me._id,
      recordedAt: now,
    };
    if (idx >= 0) state.attendance[idx] = record;
    else state.attendance.push(record);

    const enrollment = state.enrollments.find((e) => e._id === payload.enrollmentId);
    if (enrollment) {
      const wasChargeable = previous === 'present' || previous === 'absent';
      const isChargeable = payload.status === 'present' || payload.status === 'absent';
      if (isChargeable && !wasChargeable) {
        enrollment.entitlement.consumed += 1;
        enrollment.entitlement.remaining = Math.max(
          0,
          enrollment.entitlement.granted - enrollment.entitlement.consumed,
        );
      } else if (wasChargeable && payload.status === 'on_leave') {
        enrollment.entitlement.consumed = Math.max(0, enrollment.entitlement.consumed - 1);
        enrollment.entitlement.remaining = Math.max(
          0,
          enrollment.entitlement.granted - enrollment.entitlement.consumed,
        );
      }
    }
    if (session.status === 'scheduled') {
      const roster = state.enrollments.filter((e) => e.classId === session.classId && e.status === 'active');
      const marked = new Set(
        state.attendance.filter((a) => a.sessionId === sessionId).map((a) => a.enrollmentId),
      );
      if (roster.length > 0 && roster.every((row) => marked.has(row._id))) {
        session.status = 'completed';
      }
    }
    saveState(state);
    return record;
  },

  hasApprovedLeave(enrollmentId: string, sessionStart: number): boolean {
    const dayStart = new Date(sessionStart * 1000);
    dayStart.setHours(0, 0, 0, 0);
    const day = Math.floor(dayStart.getTime() / 1000);
    return state.leaveRequests.some(
      (r) =>
        r.enrollmentId === enrollmentId &&
        r.status === 'approved' &&
        r.leaveStart <= day + 86399 &&
        r.leaveEnd >= day,
    );
  },

  updateSessionDelivery(
    sessionId: string,
    payload: {
      actualCoachId?: string;
      assistantCoachId?: string | null;
      status?: ClassSession['status'];
      branchLocationId?: string | null;
    },
  ): ClassSession {
    const session = state.sessions.find((s) => s._id === sessionId);
    if (!session) throw new Error('Session not found');
    if (payload.actualCoachId !== undefined) {
      const scheduled = session.coachAssignment.scheduledCoachId;
      if (!payload.actualCoachId || payload.actualCoachId === scheduled) {
        delete session.coachAssignment.actualCoachId;
      } else {
        session.coachAssignment.actualCoachId = payload.actualCoachId;
      }
    }
    if (payload.assistantCoachId !== undefined) {
      if (payload.assistantCoachId) session.coachAssignment.assistantCoachId = payload.assistantCoachId;
      else delete session.coachAssignment.assistantCoachId;
    }
    if (payload.status) session.status = payload.status;
    if (payload.branchLocationId !== undefined) {
      session.branchLocationId = payload.branchLocationId || undefined;
      session.location = locationName(payload.branchLocationId);
    }
    session.updatedAt = Math.floor(Date.now() / 1000);
    saveState(state);
    return session;
  },

  getSessionContext(sessionId: string): SessionContext {
    state.sessions = normalizeSessionsForToday(state.sessions);
    const session = state.sessions.find((s) => s._id === sessionId);
    if (!session) throw new Error('Session not found');
    const cls = state.classes.find((c) => c._id === session.classId);
    const previous = state.sessions
      .filter((s) => s.classId === session.classId && s.scheduledStart < session.scheduledStart && s.status !== 'cancelled')
      .sort((a, b) => b.scheduledStart - a.scheduledStart)[0];
    const roster = this.buildClassRoster(session.classId);
    const marks = this.listSessionAttendance(sessionId);
    const markMap = Object.fromEntries(marks.map((m) => [m.enrollmentId, m.status]));
    const active = roster.filter((row) => row.enrollment.status === 'active');
    const unmarkedCount = active.filter((row) => !markMap[row.enrollment._id]).length;
    const scheduledCoachId = session.coachAssignment.scheduledCoachId;
    const actualCoachId = session.coachAssignment.actualCoachId ?? scheduledCoachId;
    const attendanceState: SessionContext['attendanceState'] =
      unmarkedCount === 0 && active.length > 0 ? 'complete' : unmarkedCount === active.length ? 'unmarked' : 'partial';
    const context: SessionContext = {
      session: {
        _id: session._id,
        classId: session.classId,
        scheduledStart: session.scheduledStart,
        scheduledEnd: session.scheduledEnd,
        durationMinutes: session.durationMinutes,
        status: session.status,
        notes: session.notes,
        cancellation: session.cancellation,
        teaching: session.teaching,
        coachAssignment: session.coachAssignment,
        branchLocationId: session.branchLocationId ?? cls?.branchLocationId,
        location: session.location || cls?.location,
      },
      class: cls
        ? {
            _id: cls._id,
            name: cls.name,
            danceSportType: cls.danceSportType,
            coachId: cls.coachId,
            coachIds: coachIdsOf(cls),
            assistantIds: assistantIdsOf(cls),
            classCoachName: personName(cls.coachId),
            branchLocationId: cls.branchLocationId,
            location: cls.location,
          }
        : null,
      scheduledCoach: { _id: scheduledCoachId, name: DEMO_USERS.find((u) => u._id === scheduledCoachId)?.name },
      actualCoach: { _id: actualCoachId, name: DEMO_USERS.find((u) => u._id === actualCoachId)?.name },
      replacement: actualCoachId !== scheduledCoachId,
      previousTeaching: previous
        ? {
            sessionId: previous._id,
            scheduledStart: previous.scheduledStart,
            topics: previous.teaching?.topics ?? [],
            summary: previous.teaching?.summary,
            homework: previous.teaching?.homework,
          }
        : null,
      roster: roster.map((row) => ({
        enrollmentId: row.enrollment._id,
        studentId: row.enrollment.studentId,
        studentName: row.student.name,
        studentHonorific: row.student.honorific,
        studentPhotoFileId: row.student.photoFileId,
        studentPhotoUrl: row.student.photoUrl,
        remaining: row.enrollment.entitlement.remaining,
        granted: row.enrollment.entitlement.granted,
        enrollmentStatus: row.enrollment.status,
        attendanceStatus: markMap[row.enrollment._id] ?? 'unmarked',
        leaveEligible: this.hasApprovedLeave(row.enrollment._id, session.scheduledStart),
      })),
      unmarkedCount,
      attendanceState,
      rosterSize: active.length,
    };
    const me = demoAuth.getMe();
    if (me.role === 'student') {
      const mine = context.roster.filter((row) => row.studentId === me._id);
      if (mine.length === 0) {
        const enrolled = state.enrollments.some(
          (e) =>
            e.classId === session.classId &&
            e.studentId === me._id &&
            ['pending', 'active', 'paused'].includes(e.status),
        );
        if (!enrolled) throw new Error('Forbidden');
        return { ...context, roster: [], unmarkedCount: 0, rosterSize: 0 };
      }
      return { ...context, roster: mine, unmarkedCount: 0, rosterSize: mine.length };
    }
    return context;
  },

  updateSessionTeaching(sessionId: string, payload: TeachingContent): ClassSession {
    const session = state.sessions.find((s) => s._id === sessionId);
    if (!session) throw new Error('Session not found');
    session.teaching = payload;
    session.updatedAt = Math.floor(Date.now() / 1000);
    saveState(state);
    return session;
  },

  listEvents(): InstituteEvent[] {
    const role = demoAuth.getMe().role;
    const userId = demoAuth.getMe()._id;
    const rows = state.events.map(hydrateEvent);
    if (role === 'coach') {
      return rows.filter((e) => {
        const assigned =
          e.coachAssignment?.scheduledCoachId === userId ||
          e.coachAssignment?.actualCoachId === userId ||
          e.responsibleUserId === userId;
        const open = !e.coachAssignment?.scheduledCoachId && !e.responsibleUserId;
        return assigned || open;
      });
    }
    return rows;
  },

  createEvent(payload: {
    name: string;
    eventType: InstituteEvent['eventType'];
    scheduledStart: number;
    durationMinutes: number;
    price?: number;
    scheduledCoachId?: string | null;
    responsibleUserId?: string | null;
    description?: string;
    capacity?: number;
    branchLocationId?: string | null;
    location?: string;
  }): InstituteEvent {
    const now = Math.floor(Date.now() / 1000);
    const event: InstituteEvent = {
      _id: uid('demo-event'),
      name: payload.name,
      eventType: payload.eventType,
      description: payload.description,
      scheduledStart: payload.scheduledStart,
      scheduledEnd: payload.scheduledStart + payload.durationMinutes * 60,
      durationMinutes: payload.durationMinutes,
      price: payload.price ?? 0,
      coachAssignment: payload.scheduledCoachId
        ? { scheduledCoachId: payload.scheduledCoachId }
        : undefined,
      responsibleUserId: payload.responsibleUserId || undefined,
      capacity: payload.capacity,
      branchLocationId: payload.branchLocationId || undefined,
      location: locationName(payload.branchLocationId) ?? payload.location,
      status: 'scheduled',
      createdAt: now,
      updatedAt: now,
    };
    state.events.unshift(event);
    saveState(state);
    return hydrateEvent(event);
  },

  updateEvent(eventId: string, payload: Partial<InstituteEvent>): InstituteEvent {
    const event = state.events.find((e) => e._id === eventId);
    if (!event) throw new Error('Event not found');
    Object.assign(event, payload, { updatedAt: Math.floor(Date.now() / 1000) });
    if (payload.scheduledStart != null || payload.durationMinutes != null) {
      event.scheduledEnd = event.scheduledStart + event.durationMinutes * 60;
    }
    if (payload.branchLocationId !== undefined) {
      event.location = locationName(payload.branchLocationId) ?? event.location;
    }
    if (payload.responsibleUserId === null) {
      event.responsibleUserId = undefined;
      event.responsibleName = undefined;
    }
    saveState(state);
    return hydrateEvent(event);
  },

  cancelEvent(eventId: string, payload?: { reason?: string; source?: 'institute' | 'coach' }): InstituteEvent {
    const event = state.events.find((e) => e._id === eventId);
    if (!event) throw new Error('Event not found');
    event.status = 'cancelled';
    event.cancellation = {
      source: payload?.source ?? 'institute',
      reason: payload?.reason,
      cancelledBy: demoAuth.getMe()._id,
      cancelledAt: Math.floor(Date.now() / 1000),
    };
    saveState(state);
    return event;
  },

  listEventRegistrations(eventId: string): EventRegistration[] {
    return state.eventRegs.filter((r) => r.eventId === eventId);
  },

  registerForEvent(
    eventId: string,
    studentId?: string,
    discount?: { discountId?: string; discountAmount?: number; discountReason?: string },
  ): EventRegistration {
    const id = studentId ?? demoAuth.getMe()._id;
    const ev = state.events.find((e) => e._id === eventId);
    const originalPrice = ev?.price ?? 0;
    const discountAmount = discount?.discountAmount ?? 0;
    const price = Math.max(0, originalPrice - discountAmount);

    const existing = state.eventRegs.find((r) => r.eventId === eventId && r.studentId === id);
    if (existing) {
      existing.status = 'registered';
      existing.price = price;
      existing.originalPrice = originalPrice;
      existing.discountAmount = discountAmount;
      existing.discountReason = discount?.discountReason;
      existing.discountId = discount?.discountId;
      saveState(state);
      return existing;
    }
    const row: EventRegistration = {
      _id: uid('demo-ereg'),
      eventId,
      studentId: id,
      status: 'registered',
      price,
      originalPrice,
      discountAmount,
      discountReason: discount?.discountReason,
      discountId: discount?.discountId,
    };
    state.eventRegs.push(row);
    saveState(state);
    return row;
  },

  recordEventAttendance(
    eventId: string,
    payload: { studentId: string; status: EventRegistration['status'] },
  ): EventRegistration {
    const existing = state.eventRegs.find(
      (r) => r.eventId === eventId && r.studentId === payload.studentId,
    );
    if (existing) {
      existing.status = payload.status;
      saveState(state);
      return existing;
    }
    const row: EventRegistration = {
      _id: uid('demo-ereg'),
      eventId,
      studentId: payload.studentId,
      status: payload.status,
    };
    state.eventRegs.push(row);
    saveState(state);
    return row;
  },

  addProgressNote(studentId: string, note: string) {
    const item: TimelineItem = {
      kind: 'progress',
      at: Math.floor(Date.now() / 1000),
      title: 'Progress note',
      note,
      coachName: demoAuth.getMe().name,
    };
    state.progressNotes = state.progressNotes ?? [];
    state.progressNotes.unshift({ studentId, item });
    saveState(state);
    return item;
  },

  getMyTimeline(): StudentTimeline {
    return this.getStudentTimeline(demoAuth.getMe()._id);
  },

  getStudentTimeline(studentId: string): StudentTimeline {
    const enrollments = state.enrollments.filter((e) => e.studentId === studentId);
    const attendance = state.attendance.filter((a) => a.studentId === studentId);
    const items: TimelineItem[] = [];

    for (const record of attendance) {
      const session = state.sessions.find((s) => s._id === record.sessionId);
      const enrollment = enrollments.find((e) => e._id === record.enrollmentId);
      const cls = enrollment ? state.classes.find((c) => c._id === enrollment.classId) : undefined;
      if (!session) continue;
      const actual = session.coachAssignment.actualCoachId ?? session.coachAssignment.scheduledCoachId;
      items.push({
        kind: 'session',
        at: session.scheduledStart,
        title: cls?.name ?? 'Class session',
        subtitle: cls?.danceSportType,
        classId: cls?._id ?? enrollment?.classId,
        sessionId: session._id,
        attendance: record.status,
        topics: session.teaching?.topics,
        summary: session.teaching?.summary,
        homework: session.teaching?.homework,
        coachName: DEMO_USERS.find((u) => u._id === actual)?.name,
        substituteCoach: Boolean(session.coachAssignment.actualCoachId),
      });
    }

    for (const reg of state.eventRegs.filter((r) => r.studentId === studentId && r.status !== 'cancelled')) {
      const event = state.events.find((e) => e._id === reg.eventId);
      if (!event) continue;
      const actual = event.coachAssignment?.actualCoachId ?? event.coachAssignment?.scheduledCoachId;
      items.push({
        kind: 'event',
        at: event.scheduledStart,
        title: event.name,
        subtitle: [event.eventType.replace(/_/g, ' '), event.location].filter(Boolean).join(' · '),
        attendance: reg.status,
        topics: event.teaching?.topics,
        summary: event.teaching?.summary,
        coachName: event.responsibleName || (actual ? DEMO_USERS.find((u) => u._id === actual)?.name : undefined),
        substituteCoach: Boolean(event.coachAssignment?.actualCoachId),
      });
    }

    for (const note of state.progressNotes ?? []) {
      if (note.studentId === studentId) items.push(note.item);
    }

    items.sort((a, b) => b.at - a.at);
    return {
      studentId,
      sessionCount: attendance.filter((a) => a.status === 'present' || a.status === 'absent').length,
      presentCount: attendance.filter((a) => a.status === 'present').length,
      eventCount: state.eventRegs.filter((r) => r.studentId === studentId && r.status !== 'cancelled').length,
      items,
    };
  },

  getLeavePolicy(studentId?: string): LeavePolicy {
    const policy = normalizeLeavePolicy(state.leavePolicy);
    const id = studentId ?? (demoAuth.getMe().role === 'student' ? demoAuth.getMe()._id : undefined);
    if (!id) return policy;
    const now = Math.floor(Date.now() / 1000);
    const bounds = seasonBounds(now, policy.seasonMonths);
    const usedRequests = countLeaveRequestsInSeason(
      state.leaveRequests.filter((r) => r.studentId === id),
      bounds.start,
      bounds.end,
    );
    const remainingRequests = Math.max(0, policy.maxLeaveDaysPerSeason - usedRequests);
    return {
      ...policy,
      season: {
        start: bounds.start,
        end: bounds.end,
        usedDays: usedRequests,
        remainingDays: remainingRequests,
        usedRequests,
        remainingRequests,
      },
    };
  },

  updateLeavePolicy(payload: Partial<LeavePolicy>): LeavePolicy {
    if (!isOwner(demoAuth.getMe().role)) {
      throw new Error('Only the owner can update leave policy');
    }
    state.leavePolicy = normalizeLeavePolicy({ ...state.leavePolicy, ...payload });
    saveState(state);
    return this.getLeavePolicy();
  },

  listMyLeaveRequests(studentId: string): LeaveRequest[] {
    return this.enrichLeave(
      state.leaveRequests.filter((r) => r.studentId === studentId),
    );
  },

  listLeaveRequests(status?: string): LeaveRequest[] {
    const rows = status
      ? state.leaveRequests.filter((r) => r.status === status)
      : state.leaveRequests;
    return this.enrichLeave([...rows].sort((a, b) => b.createdAt - a.createdAt));
  },

  enrichLeave(rows: LeaveRequest[]): LeaveRequest[] {
    return rows.map((r) => ({
      ...r,
      className: state.classes.find((c) => c._id === r.classId)?.name,
      studentName: personName(r.studentId),
      studentEmail: state.people.find((p) => p._id === r.studentId)?.email,
    }));
  },

  createLeaveRequest(payload: {
    studentId: string;
    enrollmentId: string;
    leaveStart: number;
    leaveEnd: number;
    note?: string;
  }): LeaveRequest {
    const enrollment = state.enrollments.find((e) => e._id === payload.enrollmentId);
    if (!enrollment || enrollment.studentId !== payload.studentId) {
      throw new Error('Enrollment not found');
    }
    const startDay = new Date(payload.leaveStart * 1000);
    startDay.setHours(0, 0, 0, 0);
    const endDay = new Date(payload.leaveEnd * 1000);
    endDay.setHours(23, 59, 59, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const noticeDays = Math.floor((startDay.getTime() - today.getTime()) / 86400000);
    const durationDays = inclusiveDays(payload.leaveStart, payload.leaveEnd);
    const institute = normalizeLeavePolicy(state.leavePolicy);
    const minNotice = enrollment.leavePolicy?.minNoticeDays ?? institute.minNoticeDays;
    const maxDuration = enrollment.leavePolicy?.maxDurationDays ?? institute.maxDurationDays;
    if (noticeDays < minNotice) {
      throw new Error(`Minimum notice is ${minNotice} days`);
    }
    if (durationDays > maxDuration) {
      throw new Error(`Leave cannot exceed ${maxDuration} days`);
    }
    const bounds = seasonBounds(payload.leaveStart, institute.seasonMonths);
    const seasonRows = state.leaveRequests.filter((r) => r.studentId === payload.studentId);
    const usedRequests = countLeaveRequestsInSeason(seasonRows, bounds.start, bounds.end);
    if (usedRequests >= institute.maxLeaveDaysPerSeason) {
      const remaining = Math.max(0, institute.maxLeaveDaysPerSeason - usedRequests);
      throw new Error(
        `Seasonal leave limit is ${institute.maxLeaveDaysPerSeason} requests every ${institute.seasonMonths} months (${remaining} remaining)`,
      );
    }
    const now = Math.floor(Date.now() / 1000);
    const row: LeaveRequest = {
      _id: uid('demo-leave'),
      studentId: payload.studentId,
      classId: enrollment.classId,
      enrollmentId: enrollment._id,
      leaveStart: Math.floor(startDay.getTime() / 1000),
      leaveEnd: Math.floor(endDay.getTime() / 1000),
      noticeDays,
      policyMinNoticeDays: minNotice,
      status: 'pending',
      note: payload.note,
      createdAt: now,
      updatedAt: now,
    };
    state.leaveRequests.unshift(row);
    saveState(state);
    return this.enrichLeave([row])[0];
  },

  reviewLeaveRequest(leaveRequestId: string, status: 'approved' | 'denied'): LeaveRequest {
    const row = state.leaveRequests.find((r) => r._id === leaveRequestId);
    if (!row) throw new Error('Leave request not found');
    if (row.status !== 'pending') throw new Error('Leave request already reviewed');
    const now = Math.floor(Date.now() / 1000);
    row.status = status;
    row.reviewedBy = demoAuth.getMe()._id;
    row.reviewedAt = now;
    row.updatedAt = now;
    if (status === 'approved') {
      const sessions = state.sessions.filter(
        (s) =>
          s.classId === row.classId &&
          s.status === 'scheduled' &&
          s.scheduledStart >= row.leaveStart &&
          s.scheduledStart <= row.leaveEnd,
      );
      for (const session of sessions) {
        try {
          this.recordAttendance(session._id, {
            enrollmentId: row.enrollmentId,
            studentId: row.studentId,
            status: 'on_leave',
          });
        } catch {
          /* ignore */
        }
      }
    }
    saveState(state);
    return this.enrichLeave([row])[0];
  },
};

export const demoNotifications = {
  list(): NotificationRecord[] {
    return state.notifications;
  },

  listMine(): NotificationRecord[] {
    return state.notifications;
  },

  create(payload: CreateNotificationPayload) {
    const now = Math.floor(Date.now() / 1000);
    const notification: NotificationRecord = {
      _id: uid('demo-note'),
      ...payload,
      status: 'sent',
      createdBy: demoAuth.getMe()._id,
      sentAt: now,
      createdAt: now,
    };
    state.notifications.unshift(notification);
    saveState(state);
    return { notification, recipientCount: DEMO_USERS.filter((u) => u.role === 'student').length, sent: 2, failed: 0 };
  },
};

export const demoAnnouncements = {
  list(): Announcement[] {
    return state.announcements;
  },

  listMine(): Announcement[] {
    const me = demoAuth.getMe();
    const now = Math.floor(Date.now() / 1000);
    const classIds = state.enrollments
      .filter((e) => e.studentId === me._id && (e.status === 'active' || e.status === 'pending' || e.status === 'paused'))
      .map((e) => e.classId);
    return state.announcements.filter((row) => {
      if (!row.active) return false;
      if (row.expiresAt && row.expiresAt <= now) return false;
      if (row.audience === 'all') return true;
      if (row.audience === 'students') return me.role === 'student';
      if (row.audience === 'coaches') return me.role === 'coach';
      if (row.audience === 'operators') return me.role === 'operator';
      if (row.audience === 'class') return Boolean(row.classId && classIds.includes(row.classId));
      return false;
    });
  },

  create(payload: CreateAnnouncementPayload): Announcement {
    const row: Announcement = {
      _id: uid('demo-banner'),
      message: payload.message.trim(),
      audience: payload.audience,
      classId: payload.classId,
      className: payload.classId ? state.classes.find((c) => c._id === payload.classId)?.name : undefined,
      active: true,
      expiresAt: payload.expiresAt,
      createdBy: demoAuth.getMe()._id,
      createdAt: Math.floor(Date.now() / 1000),
    };
    state.announcements.unshift(row);
    saveState(state);
    return row;
  },

  deactivate(id: string): Announcement {
    const row = state.announcements.find((item) => item._id === id);
    if (!row) throw new Error('not found');
    row.active = false;
    saveState(state);
    return row;
  },

  getAuthBanner(): Announcement | null {
    const now = Math.floor(Date.now() / 1000);
    const active = state.announcements.filter((row) => {
      if (!row.active) return false;
      if (row.expiresAt && row.expiresAt <= now) return false;
      return row.audience === 'auth';
    });
    return active[0] ?? null;
  },
};

export const demoBilling = {
  getSummary(userId: string): BillingSummary {
    const dueInvoices = state.invoices.filter((i) => i.studentId === userId && i.status === 'due');
    const enrollments = state.enrollments.filter((e) => e.studentId === userId && e.status === 'active');
    const minRemaining = enrollments.length
      ? Math.min(...enrollments.map((e) => e.entitlement.remaining))
      : state.pricing.sessionsPerPaymentUnit;

    return {
      sessionsPerPaymentUnit: state.pricing.sessionsPerPaymentUnit,
      pricePerUnit: state.pricing.pricePerUnit,
      currency: 'IRT',
      totalDue: dueInvoices.reduce((s, i) => s + i.amount, 0),
      openInvoices: dueInvoices.length,
      overdueInvoices: dueInvoices.filter((i) => i.dueAt < Math.floor(Date.now() / 1000)).length,
      nextPaymentSessionsRemaining: minRemaining,
      overduePolicy: state.settings.overduePolicy,
      requirePrepayOnEnrollment: state.settings.requirePrepayOnEnrollment,
    };
  },

  listInvoices(userId: string): StudentInvoice[] {
    return state.invoices.filter((i) => i.studentId === userId);
  },

  listAdminInvoices(status?: string): StudentInvoice[] {
    return status ? state.invoices.filter((i) => i.status === status) : state.invoices;
  },

  recordPayment(invoiceId: string, _method: PaymentMethod, _note?: string) {
    const invoice = state.invoices.find((i) => i._id === invoiceId && i.status === 'due');
    if (!invoice) throw new Error('Invoice not found');
    invoice.status = 'paid';
    invoice.paidAt = Math.floor(Date.now() / 1000);
    const enrollment = state.enrollments.find((e) => e._id === invoice.enrollmentId);
    if (enrollment) {
      enrollment.status = 'active';
      enrollment.entitlement = {
        granted: invoice.sessionsIncluded,
        consumed: 0,
        remaining: invoice.sessionsIncluded,
      };
    }
    saveState(state);
    return { invoice };
  },

  getPricing(): PricingPolicy {
    return state.pricing;
  },

  updatePricing(payload: Partial<PricingPolicy>): PricingPolicy {
    state.pricing = { ...state.pricing, ...payload, currency: 'IRT' };
    saveState(state);
    return state.pricing;
  },

  getSettings(): PaymentSettings {
    return state.settings;
  },

  updateSettings(payload: Partial<PaymentSettings>): PaymentSettings {
    state.settings = { ...state.settings, ...payload };
    saveState(state);
    return state.settings;
  },

  updateClassPricing(classId: string, payload: { sessionsPerPaymentUnit: number; pricePerUnit: number }) {
    const cls = state.classes.find((c) => c._id === classId);
    if (cls) cls.pricing = { ...payload, currency: 'IRT' };
    saveState(state);
    return cls;
  },

  listAgreements(): CoachAgreement[] {
    return state.agreements;
  },

  createAgreement(payload: {
    coachId: string;
    rateIrt?: number;
    kind?: CoachAgreement['kind'];
    staffKind?: CoachAgreement['staffKind'];
    payType?: CoachAgreement['payType'];
    percentage?: number;
  }): CoachAgreement {
    const staffKind = payload.staffKind ?? 'coach';
    const existing = state.agreements.find(
      (row) => row.coachId === payload.coachId && (row.staffKind ?? 'coach') === staffKind && !row.effectiveTo,
    );
    const payType = payload.payType ?? 'fixed';
    if (existing) {
      existing.kind = payload.kind ?? existing.kind;
      existing.staffKind = staffKind;
      existing.payType = payType;
      existing.percentage = payload.percentage;
      existing.rateIrt = payload.rateIrt ?? 0;
      saveState(state);
      return existing;
    }
    const row: CoachAgreement = {
      _id: uid('demo-agree'),
      coachId: payload.coachId,
      kind: payload.kind ?? 'class_hourly',
      staffKind,
      payType,
      percentage: payload.percentage,
      rateIrt: payload.rateIrt ?? 0,
      effectiveFrom: Math.floor(Date.now() / 1000),
    };
    state.agreements.unshift(row);
    saveState(state);
    return row;
  },

  listLedger(): LedgerEntry[] {
    const rows = state.ledger;
    const me = demoAuth.getMe();
    if (me.role === 'operator') {
      return rows.filter((row) => ['vending', 'supplies', 'other'].includes(row.source));
    }
    return rows;
  },

  createLedger(payload: {
    kind: LedgerKind;
    source: LedgerSource;
    amountIrt: number;
    period?: LedgerPeriod;
    periodStart: number;
    note?: string;
  }): LedgerEntry {
    const me = demoAuth.getMe();
    if (me.role === 'operator' && !['vending', 'supplies', 'other'].includes(payload.source)) {
      throw new Error('This ledger reason is not allowed for personnel');
    }
    const current = state.shareConfigs[0];
    const share =
      payload.kind === 'income' && payload.source === 'vending' && current
        ? {
            configId: current._id,
            operatorPercentage: current.operatorPercentage,
            institutePercentage: current.institutePercentage,
            operatorShareIrt: Math.round((payload.amountIrt * current.operatorPercentage) / 100),
            instituteShareIrt: payload.amountIrt - Math.round((payload.amountIrt * current.operatorPercentage) / 100),
          }
        : undefined;
    const row: LedgerEntry = {
      _id: uid('demo-led'),
      ...payload,
      period: payload.period ?? 'daily',
      recordedBy: me._id,
      recordedByName: me.name,
      share,
      createdAt: Math.floor(Date.now() / 1000),
    };
    state.ledger.unshift(row);
    saveState(state);
    return row;
  },

  getCompensation(coachId: string): CompensationResponse {
    const agreement = state.agreements
      .filter((a) => a.coachId === coachId)
      .sort((a, b) => b.effectiveFrom - a.effectiveFrom)[0];
    const sessions = state.sessions.filter((s) => s.status !== 'cancelled');
    const sessionCountByClass = new Map<string, number>();
    for (const s of sessions) sessionCountByClass.set(s.classId, (sessionCountByClass.get(s.classId) ?? 0) + 1);
    const revenueByClass = new Map<string, number>();
    for (const inv of state.invoices.filter((i) => i.status === 'paid')) {
      revenueByClass.set(inv.classId, (revenueByClass.get(inv.classId) ?? 0) + inv.amount);
    }
    const vendingTotal = state.ledger
      .filter((e) => e.kind === 'income' && e.source === 'vending')
      .reduce((s, e) => s + e.amountIrt, 0);

    const lines = sessions
      .filter((s) => s.coachAssignment.scheduledCoachId === coachId || s.coachAssignment.actualCoachId === coachId)
      .map((s) => {
        const classShare = (revenueByClass.get(s.classId) ?? 0) / Math.max(1, sessionCountByClass.get(s.classId) ?? 1);
        let amountIrt = 0;
        let hourlyRateIrt = agreement?.rateIrt ?? 400_000;
        if (agreement?.payType === 'percentage') {
          amountIrt = Math.round((classShare * (agreement.percentage ?? 0)) / 100);
          hourlyRateIrt = 0;
        } else if (agreement?.staffKind === 'operator') {
          amountIrt = 0;
        } else {
          amountIrt = Math.round((s.durationMinutes / 60) * hourlyRateIrt);
        }
        return {
          sessionId: s._id,
          classId: s.classId,
          className: state.classes.find((c) => c._id === s.classId)?.name ?? 'Class',
          scheduledStart: s.scheduledStart,
          durationMinutes: s.durationMinutes,
          replacement: Boolean(s.coachAssignment.actualCoachId),
          hourlyRateIrt,
          amountIrt,
        };
      });
    let amountIrt = lines.reduce((s, l) => s + l.amountIrt, 0);
    if (agreement?.staffKind === 'operator') {
      amountIrt = agreement.payType === 'percentage'
        ? Math.round((vendingTotal * (agreement.percentage ?? 0)) / 100)
        : agreement.rateIrt;
    }
    const minutes = lines.reduce((s, l) => s + l.durationMinutes, 0);
    const self = {
      coachId,
      coachName: DEMO_USERS.find((u) => u._id === coachId)?.name,
      sessionsDelivered: lines.length,
      hours: Number((minutes / 60).toFixed(2)),
      amountIrt,
      lines,
    };
    const now = Math.floor(Date.now() / 1000);
    return { currency: 'IRT', period: { from: now - 86400 * 30, to: now }, coaches: [self], self };
  },

  getOverview(): InstituteOverview {
    const paid = state.invoices.filter((i) => i.status === 'paid');
    const due = state.invoices.filter((i) => i.status === 'due');
    const studentTuition = paid.reduce((s, i) => s + i.amount, 0);
    const paidDiscount = paid.reduce(
      (s, i) => s + (i.discountAmount ?? (i.originalAmount && i.originalAmount > i.amount ? i.originalAmount - i.amount : 0)),
      0,
    );
    const paidGrossTuition = paid.reduce(
      (s, i) => s + (i.originalAmount ?? (i.amount + (i.discountAmount ?? 0))),
      0,
    );
    const dueDiscount = due.reduce(
      (s, i) => s + (i.discountAmount ?? (i.originalAmount && i.originalAmount > i.amount ? i.originalAmount - i.amount : 0)),
      0,
    );
    const vending = state.ledger
      .filter((e) => e.kind === 'income' && e.source === 'vending')
      .reduce((s, e) => s + e.amountIrt, 0);
    const otherIncome = state.ledger
      .filter((e) => e.kind === 'income' && e.source !== 'vending')
      .reduce((s, e) => s + e.amountIrt, 0);
    const recordedExpense = state.ledger.filter((e) => e.kind === 'expense').reduce((s, e) => s + e.amountIrt, 0);
    const coachPay = this.getCompensation('demo-coach-001').self.amountIrt;
    const now = Math.floor(Date.now() / 1000);

    return {
      currency: 'IRT',
      period: { from: now - 86400 * 30, to: now },
      income: {
        studentTuition,
        studentDiscount: paidDiscount,
        studentGrossTuition: paidGrossTuition,
        vending,
        other: otherIncome,
        total: studentTuition + vending + otherIncome,
        grossTotal: paidGrossTuition + vending + otherIncome,
      },
      expenses: { coachCompensation: coachPay, recorded: recordedExpense, total: coachPay + recordedExpense },
      openStudentDue: due.reduce((s, i) => s + i.amount, 0),
      overdueDue: 0,
      totalDiscount: paidDiscount,
      openDiscount: dueDiscount,
      net: studentTuition + vending + otherIncome - coachPay - recordedExpense,
      classes: state.classes.map((cls) => {
        const classPaid = paid.filter((i) => i.classId === cls._id);
        const revenue = classPaid.reduce((s, i) => s + i.amount, 0);
        const discount = classPaid.reduce(
          (s, i) => s + (i.discountAmount ?? (i.originalAmount && i.originalAmount > i.amount ? i.originalAmount - i.amount : 0)),
          0,
        );
        const gross = classPaid.reduce(
          (s, i) => s + (i.originalAmount ?? (i.amount + (i.discountAmount ?? 0))),
          0,
        );
        const open = due.filter((i) => i.classId === cls._id).reduce((s, i) => s + i.amount, 0);
        const coachCost = Math.round(coachPay / Math.max(1, state.classes.length));
        return {
          classId: cls._id,
          className: cls.name,
          pricing: cls.pricing ?? state.pricing,
          studentRevenue: revenue,
          studentDiscount: discount,
          studentGrossRevenue: gross || (revenue + discount),
          openStudentDue: open,
          coachCost,
          sessionsDelivered: state.sessions.filter((s) => s.classId === cls._id).length,
          net: revenue - coachCost,
        };
      }),
      coaches: this.getCompensation('demo-coach-001').coaches,
      entitlements: state.enrollments.map((e) => ({
        enrollmentId: e._id,
        studentId: e.studentId,
        studentName: DEMO_USERS.find((u) => u._id === e.studentId)?.name ?? 'Student',
        classId: e.classId,
        className: state.classes.find((c) => c._id === e.classId)?.name ?? 'Class',
        status: e.status,
        entitlement: e.entitlement,
      })),
    };
  },
};

export const demoFiles = {
  list(query?: { classId?: string; ownerType?: ClassFile['ownerType']; ownerId?: string }): ClassFile[] {
    return state.files.filter((f) => {
      if (query?.classId && f.classId !== query.classId && f.ownerId !== query.classId) return false;
      if (query?.ownerType && f.ownerType !== query.ownerType) return false;
      if (query?.ownerId && f.ownerId !== query.ownerId) return false;
      return true;
    });
  },

  upload(payload: {
    file: File;
    ownerType: ClassFile['ownerType'];
    ownerId: string;
    classId?: string;
  }): ClassFile {
    const row: ClassFile = {
      _id: uid('demo-file'),
      ownerType: payload.ownerType,
      ownerId: payload.ownerId,
      classId: payload.classId ?? (payload.ownerType === 'class' ? payload.ownerId : undefined),
      name: payload.file.name,
      mimeType: payload.file.type || 'application/octet-stream',
      size: payload.file.size,
      uploadedBy: demoAuth.getMe()._id,
      createdAt: Math.floor(Date.now() / 1000),
    };
    state.files.unshift(row);
    saveState(state);
    return row;
  },
};

function decorateDemoSession(session: ClassSession): DashboardSession {
  const cls = state.classes.find((c) => c._id === session.classId);
  const roster = state.enrollments.filter((e) => e.classId === session.classId && e.status === 'active');
  const marked = new Set(state.attendance.filter((a) => a.sessionId === session._id).map((a) => a.enrollmentId));
  const unmarkedCount = roster.filter((row) => !marked.has(row._id)).length;
  const scheduledCoachId = session.coachAssignment.scheduledCoachId;
  const actualCoachId = session.coachAssignment.actualCoachId ?? scheduledCoachId;
  const assistantCoachId = session.coachAssignment.assistantCoachId;
  const scheduledCoach = personVisual(scheduledCoachId);
  const actualCoach = personVisual(actualCoachId);
  const assistantCoach = assistantCoachId ? personVisual(assistantCoachId) : undefined;
  const coachesList = [
    actualCoach ? { ...actualCoach, role: 'actual' as const } : null,
    scheduledCoach && actualCoachId !== scheduledCoachId ? { ...scheduledCoach, role: 'scheduled' as const } : null,
    assistantCoach ? { ...assistantCoach, role: 'assistant' as const } : null,
  ].filter(Boolean) as any[];

  return {
    _id: session._id,
    classId: session.classId,
    className: cls?.name,
    danceSportType: cls?.danceSportType,
    scheduledStart: session.scheduledStart,
    scheduledEnd: session.scheduledEnd,
    durationMinutes: session.durationMinutes,
    status: session.status,
    scheduledCoachId,
    actualCoachId,
    scheduledCoachName: scheduledCoach?.name,
    actualCoachName: actualCoach?.name,
    assistantCoachName: assistantCoach?.name,
    coaches: coachesList,
    replacement: actualCoachId !== scheduledCoachId,
    teaching: session.teaching,
    unmarkedCount,
    rosterSize: roster.length,
    attendanceState: unmarkedCount === 0 && roster.length > 0 ? 'complete' : unmarkedCount === roster.length ? 'unmarked' : 'partial',
    location: session.location || cls?.location,
  };
}

function currentShare(): RevenueShareConfig {
  return state.shareConfigs[0] ?? {
    _id: 'demo-share-001',
    operatorPercentage: 40,
    institutePercentage: 60,
    effectiveFrom: Math.floor(Date.now() / 1000),
  };
}

function settleDemo(from: number, to: number) {
  const current = currentShare();
  const sales = state.ledger.filter(
    (e) => e.kind === 'income' && e.source === 'vending' && e.periodStart >= from && e.periodStart <= to,
  );
  const grossSales = sales.reduce((s, e) => s + e.amountIrt, 0);
  const operatorShare = sales.reduce(
    (s, e) => s + (e.share?.operatorShareIrt ?? Math.round((e.amountIrt * current.operatorPercentage) / 100)),
    0,
  );
  return {
    from,
    to,
    transactionCount: sales.length,
    grossSales,
    operatorPercentage: current.operatorPercentage,
    institutePercentage: current.institutePercentage,
    operatorShare,
    instituteShare: grossSales - operatorShare,
    effectiveFrom: current.effectiveFrom,
    configId: current._id,
  };
}

export const demoDashboard = {
  getHome(): RoleDashboard {
    const me = demoAuth.getMe();
    if (isAdminLike(me.role)) return this.getAdmin();
    if (me.role === 'coach') return this.getCoach(me._id);
    if (me.role === 'operator') return this.getOperator();
    return this.getStudent(me._id);
  },

  getAdmin(): AdminDashboard {
    const today = resolveScopeBounds('today');
    const sessions = demoAcademic.listSessionsInRange(today.from, today.to).map(decorateDemoSession);
    const events = state.events.filter((e) => e.scheduledStart >= today.from && e.scheduledStart <= today.to);
    const due = state.invoices.filter((i) => i.status === 'due');
    const incomplete = sessions.filter((s) => s.status === 'scheduled' && s.attendanceState !== 'complete');
    const replacements = sessions.filter((s) => s.replacement);
    const cancelled = sessions.filter((s) => s.status === 'cancelled');
    const exceptions = [
      incomplete.length
        ? { code: 'attendance_incomplete' as const, count: incomplete.length, severity: 'warning' as const, href: '/classes', sessionIds: incomplete.map((s) => s._id) }
        : null,
      replacements.length
        ? { code: 'coach_replacement' as const, count: replacements.length, severity: 'info' as const, href: '/classes', sessionIds: replacements.map((s) => s._id) }
        : null,
      due.length
        ? { code: 'payment_due' as const, count: due.length, severity: 'warning' as const, href: '/billing' }
        : null,
      cancelled.length
        ? { code: 'session_cancelled' as const, count: cancelled.length, severity: 'info' as const, href: '/classes', sessionIds: cancelled.map((s) => s._id) }
        : null,
      events.length
        ? { code: 'event_changed' as const, count: events.length, severity: 'info' as const, href: '/events' }
        : null,
    ].filter(Boolean) as AdminDashboard['exceptions'];

    return {
      role: 'admin',
      today: {
        from: today.from,
        to: today.to,
        sessions: sessions.length,
        events: events.length,
        attendanceCompleted: sessions.filter((s) => s.attendanceState === 'complete' && s.status !== 'cancelled').length,
        attendanceTotal: sessions.filter((s) => s.status !== 'cancelled').length,
        paymentsDue: due.length,
      },
      overview: {
        enrolledStudents: new Set(
          state.enrollments.filter((e) => ['pending', 'active', 'paused'].includes(e.status)).map((e) => e.studentId),
        ).size,
        activeClasses: state.classes.filter((c) => c.status === 'active').length,
        sessionsConsumed: state.enrollments.reduce((sum, e) => sum + e.entitlement.consumed, 0),
        pendingInvoiceAmount: due.reduce((sum, i) => sum + i.amount, 0),
        pendingInvoiceCount: due.length,
        coachCount: state.people.filter((u) => u.role === 'coach').length,
        disciplineCount: new Set(state.classes.map((c) => c.danceSportType)).size,
      },
      enrollments: state.enrollments
        .filter((e) => ['pending', 'active', 'paused'].includes(e.status))
        .slice(0, 8)
        .map((e) => {
          const pack = state.pricing.sessionsPerPaymentUnit;
          const cycleUsed = e.entitlement.consumed % pack;
          const invoiceDue = due.some((i) => i.enrollmentId === e._id) || e.entitlement.remaining <= 0;
          return {
            enrollmentId: e._id,
            studentId: e.studentId,
            studentName: personVisual(e.studentId)?.name ?? 'Student',
            studentHonorific: personVisual(e.studentId)?.honorific,
            studentPhotoFileId: personVisual(e.studentId)?.photoFileId,
            studentPhotoUrl: personVisual(e.studentId)?.photoUrl,
            className: state.classes.find((c) => c._id === e.classId)?.name ?? 'Class',
            status: e.status === 'paused' ? 'on_leave' : invoiceDue ? 'billing' : e.status,
            consumed: cycleUsed || (invoiceDue ? pack : e.entitlement.consumed),
            granted: pack,
            invoiceDue,
          };
        }),
      exceptions,
      schedule: sessions,
      sessions,
      events: events.map((e) => ({
        _id: e._id,
        name: e.name,
        eventType: e.eventType,
        scheduledStart: e.scheduledStart,
        status: e.status,
        location: e.location,
      })),
    };
  },

  getCoach(coachId: string): CoachDashboard {
    const today = resolveScopeBounds('today');
    const upcoming = demoAcademic
      .listSessionsInRange(today.from, today.to, coachId)
      .map(decorateDemoSession)
      .filter((s) => s.status !== 'cancelled')
      .sort((a, b) => a.scheduledStart - b.scheduledStart);
    const now = Math.floor(Date.now() / 1000);
    const next = upcoming.find((s) => s.scheduledEnd >= now) ?? upcoming[0] ?? null;
    const previousTeaching = next ? demoAcademic.getSessionContext(next._id).previousTeaching : null;
    return {
      role: 'coach',
      today: { from: today.from, to: today.to, sessions: upcoming.length, events: 0 },
      nextSession: next,
      previousTeaching,
      upcoming,
      unmarkedCount: upcoming.reduce((sum, s) => sum + (s.status === 'scheduled' ? s.unmarkedCount : 0), 0),
    };
  },

  getStudent(studentId: string): StudentDashboard {
    const enrollments = state.enrollments.filter((e) => e.studentId === studentId && ['pending', 'active', 'paused'].includes(e.status));
    const allowed = new Set(enrollments.map((e) => e.classId));
    const now = Math.floor(Date.now() / 1000);
    const sessions = state.sessions
      .filter((s) => allowed.has(s.classId) && s.status !== 'cancelled')
      .sort((a, b) => a.scheduledStart - b.scheduledStart);
    const next = sessions.find((s) => s.status === 'scheduled' && s.scheduledEnd >= now);
    const summary = demoBilling.getSummary(studentId);
    const packSize = Math.max(1, summary.sessionsPerPaymentUnit);
    const remainingInCycle = Math.max(0, Math.min(packSize, summary.nextPaymentSessionsRemaining));
    const openInvoices = state.invoices.filter((i) => i.studentId === studentId && i.status === 'due').length;
    const invoiceDue = openInvoices > 0 || summary.totalDue > 0;
    const usedInCycle = invoiceDue ? packSize : Math.max(0, packSize - remainingInCycle);

    const payment =
      summary.overdueInvoices && summary.overdueInvoices > 0
        ? { nextPaymentDueAfter: 'now' as const, copy: 'پرداخت شما سررسید گذشته است.', amountDueIrt: summary.totalDue }
        : summary.totalDue > 0
          ? { nextPaymentDueAfter: 'now' as const, copy: 'الان مبلغی برای پرداخت دارید.', amountDueIrt: summary.totalDue }
          : summary.nextPaymentSessionsRemaining <= 1
            ? { nextPaymentDueAfter: 'next_session' as const, copy: 'پرداخت بعدی شما بعد از جلسه بعد است.', amountDueIrt: 0 }
            : { nextPaymentDueAfter: 'none' as const, copy: 'الان بدهی ندارید.', amountDueIrt: 0 };

    const primary = enrollments[0];
    const primaryClass = primary ? state.classes.find((c) => c._id === primary.classId) : undefined;

    return {
      role: 'student',
      nextClass: next
        ? {
            sessionId: next._id,
            classId: next.classId,
            className: state.classes.find((c) => c._id === next.classId)?.name,
            scheduledStart: next.scheduledStart,
            scheduledEnd: next.scheduledEnd,
            durationMinutes: next.durationMinutes,
            coachName: personName(next.coachAssignment.actualCoachId ?? next.coachAssignment.scheduledCoachId),
            coaches: [
              personVisual(next.coachAssignment.actualCoachId ?? next.coachAssignment.scheduledCoachId),
              next.coachAssignment.assistantCoachId ? personVisual(next.coachAssignment.assistantCoachId) : null,
            ].filter(Boolean) as NonNullable<ReturnType<typeof personVisual>>[],
            status: next.status,
          }
        : null,
      classes: enrollments.map((enrollment) => {
        const cls = state.classes.find((c) => c._id === enrollment.classId);
        const records = state.attendance.filter((a) => a.studentId === studentId && state.sessions.find((s) => s._id === a.sessionId)?.classId === enrollment.classId);
        const present = records.filter((r) => r.status === 'present').length;
        const onLeave = records.filter((r) => r.status === 'on_leave').length;
        const upcoming = sessions.find((s) => s.classId === enrollment.classId && s.status === 'scheduled' && s.scheduledEnd >= now);
        const missedSession = [...sessions].reverse().find((s) => s.classId === enrollment.classId && s.scheduledStart < now);
        const missedRecord = missedSession ? records.find((r) => r.sessionId === missedSession._id) : undefined;
        const sessionWithNote = [...sessions].reverse().find((s) =>
          s.classId === enrollment.classId &&
          Boolean(s.teaching?.summary?.trim() || s.teaching?.homework?.trim() || (s.teaching?.topics && s.teaching.topics.length > 0)),
        );
        return {
          classId: enrollment.classId,
          className: cls?.name ?? 'Class',
          danceSportType: cls?.danceSportType,
          enrollmentStatus: enrollment.status,
          enrolledAt: enrollment.enrolledAt,
          coaches: cls ? staffIdsOf(cls).map((id) => personVisual(id)).filter(Boolean) as NonNullable<ReturnType<typeof personVisual>>[] : [],
          coachName: cls ? staffIdsOf(cls).map((id) => personName(id)).filter(Boolean).join('، ') : undefined,
          entitlement: { ...enrollment.entitlement },
          attendanceCopy: toFaDigits(`${present} جلسه حاضر · ${onLeave} مرخصی`),
          nextSessionAt: upcoming?.scheduledStart,
          missed:
            missedSession && (missedRecord?.status === 'absent' || missedRecord?.status === 'on_leave')
              ? {
                  sessionId: missedSession._id,
                  scheduledStart: missedSession.scheduledStart,
                  topics: missedSession.teaching?.topics ?? [],
                  summary: missedSession.teaching?.summary,
                  homework: missedSession.teaching?.homework,
                }
              : null,
          latestNote: sessionWithNote
            ? {
                sessionId: sessionWithNote._id,
                scheduledStart: sessionWithNote.scheduledStart,
                topics: sessionWithNote.teaching?.topics ?? [],
                summary: sessionWithNote.teaching?.summary,
                homework: sessionWithNote.teaching?.homework,
              }
            : null,
        };
      }),
      sessionPack: {
        packSize,
        usedInCycle,
        remainingInCycle: invoiceDue ? 0 : remainingInCycle,
        invoiceDue,
      },
      highlightClass: primaryClass
        ? {
            classId: primaryClass._id,
            className: primaryClass.name,
            enrolledAt: primary?.enrolledAt,
            danceSportType: primaryClass.danceSportType,
          }
        : undefined,
      payment,
      events: state.events
        .filter((e) => e.status === 'scheduled' && e.scheduledStart >= now)
        .slice(0, 5)
        .map((e) => ({
          _id: e._id,
          name: e.name,
          eventType: e.eventType,
          scheduledStart: e.scheduledStart,
          location: e.location,
        })),
      openInvoices,
    };
  },

  getOperator(): OperatorDashboard {
    const current = currentShare();
    const today = settleDemo(resolveScopeBounds('today').from, resolveScopeBounds('today').to);
    const week = settleDemo(resolveScopeBounds('week').from, resolveScopeBounds('week').to);
    const month = settleDemo(resolveScopeBounds('month').from, resolveScopeBounds('month').to);
    return {
      role: 'operator',
      currentSplit: current,
      todaySales: {
        grossSales: today.grossSales,
        transactionCount: today.transactionCount,
        operatorShare: today.operatorShare,
        instituteShare: today.instituteShare,
      },
      settlements: { today, week, month },
    };
  },

  getScope(period: 'today' | 'week' | 'month', at?: number): DashboardScope {
    const me = demoAuth.getMe();
    const bounds = resolveScopeBounds(period, at ? at * 1000 : Date.now());
    const coachId = me.role === 'coach' ? me._id : undefined;
    const studentAllowed =
      me.role === 'student'
        ? new Set(
            state.enrollments
              .filter((e) => e.studentId === me._id && ['pending', 'active', 'paused'].includes(e.status))
              .map((e) => e.classId),
          )
        : null;
    const sessions = demoAcademic
      .listSessionsInRange(bounds.from, bounds.to, coachId)
      .filter((s) => !studentAllowed || studentAllowed.has(s.classId))
      .map(decorateDemoSession);
    const classIds = new Set([
      ...state.classes
        .filter((c) => !coachId || classHasStaff(c, coachId))
        .filter((c) => !studentAllowed || studentAllowed.has(c._id))
        .map((c) => c._id),
      ...sessions.map((s) => s.classId),
    ]);
    const classes = state.classes
      .filter((c) => classIds.has(c._id))
      .map((cls) => {
        const classSessions = sessions.filter((s) => s.classId === cls._id);
        return {
          classId: cls._id,
          className: cls.name,
          danceSportType: cls.danceSportType,
          classCoachId: coachIdsOf(cls)[0] ?? cls.coachId,
          classCoachName: personName(coachIdsOf(cls)[0] ?? cls.coachId),
          classCoachNames: coachIdsOf(cls).map((id) => personName(id)).filter((n): n is string => Boolean(n)),
          classAssistantNames: assistantIdsOf(cls).map((id) => personName(id)).filter((n): n is string => Boolean(n)),
          classStaffIds: staffIdsOf(cls),
          location: cls.location,
          studentCount: state.enrollments.filter(
            (e) => e.classId === cls._id && ['pending', 'active', 'paused'].includes(e.status),
          ).length,
          sessionCount: classSessions.length,
          completedCount: classSessions.filter((s) => s.status === 'completed' || s.attendanceState === 'complete').length,
          cancelledCount: classSessions.filter((s) => s.status === 'cancelled').length,
          unmarkedCount: classSessions.reduce((sum, s) => sum + (s.status === 'scheduled' ? s.unmarkedCount : 0), 0),
          sessions: classSessions,
        };
      })
      .filter((row) => isAdminLike(me.role) || me.role === 'operator' || row.sessions.length > 0 || row.classStaffIds?.includes(me._id) || me.role === 'student');
    return {
      period,
      from: bounds.from,
      to: bounds.to,
      classes,
      events: state.events
        .filter((e) => e.scheduledStart >= bounds.from && e.scheduledStart <= bounds.to)
        .filter((e) => {
          if (!coachId) return true;
          const assigned =
            e.coachAssignment?.scheduledCoachId === coachId ||
            e.coachAssignment?.actualCoachId === coachId ||
            e.responsibleUserId === coachId;
          const open = !e.coachAssignment?.scheduledCoachId && !e.responsibleUserId;
          return assigned || open;
        })
        .map((e) => {
          const hydrated = hydrateEvent(e);
          const scheduledCoachId = hydrated.coachAssignment?.scheduledCoachId;
          const actualCoachId = hydrated.coachAssignment?.actualCoachId ?? scheduledCoachId;
          return {
            _id: hydrated._id,
            name: hydrated.name,
            eventType: hydrated.eventType,
            scheduledStart: hydrated.scheduledStart,
            scheduledEnd: hydrated.scheduledEnd,
            durationMinutes: hydrated.durationMinutes,
            status: hydrated.status,
            location: hydrated.location,
            scheduledCoachName: scheduledCoachId ? personName(scheduledCoachId) : undefined,
            actualCoachName: actualCoachId ? personName(actualCoachId) : undefined,
            responsibleUserId: hydrated.responsibleUserId,
            responsibleName: hydrated.responsibleName,
            replacement: Boolean(scheduledCoachId && actualCoachId && actualCoachId !== scheduledCoachId),
          };
        }),
    };
  },

  getRevenueShare() {
    return { current: currentShare(), history: state.shareConfigs };
  },

  createRevenueShare(payload: { operatorPercentage: number; institutePercentage: number; effectiveFrom?: number }) {
    if (payload.operatorPercentage + payload.institutePercentage !== 100) {
      throw new Error('Operator and institute shares must add up to 100');
    }
    const now = Math.floor(Date.now() / 1000);
    const effectiveFrom = payload.effectiveFrom ?? now;
    const previous = state.shareConfigs[0];
    if (previous && !previous.effectiveTo) previous.effectiveTo = effectiveFrom - 1;
    const created: RevenueShareConfig = {
      _id: uid('demo-share'),
      operatorPercentage: payload.operatorPercentage,
      institutePercentage: payload.institutePercentage,
      effectiveFrom,
      createdBy: demoAuth.getMe()._id,
      createdAt: now,
    };
    state.shareConfigs.unshift(created);
    saveState(state);
    return created;
  },
};
